/**
 * clinicalTools.js — Реальные клинические инструменты
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Заменяет ВСЕ заглушки на рабочий код.
 * Использует:
 *  - cornerstone cache для доступа к volume scalarData
 *  - aiModule.js для calciumScoring, segmentCardiacChambers, MedSAM
 *  - Собственные алгоритмы: эмфизема, CTR, BMD, пневмоторакс, region grow
 */

import { estimateEF, clearSamCache, calciumScoring, segmentCardiacChambers, isModelAvailable, samEncodeImage, samDecodePoints } from '../ai/aiModule.js';   // [12.0.56] runSegmentation3DLoop, loadModel, hasAlgorithmicFallback больше не нужны — путь карточек ИИ удалён
import { getRenderingEngine, getViewportIds } from '../core/viewport.js';
import { getActiveOrFirstViewportId } from '../core/viewportState.js';
import { найтиТом, данныеТома, требуетсяКТ, индексВМир } from '../core/volumePick.js';   // [12.0.52]
import { категорияАгатстона, уровеньАгатстона } from '../ai/calciumScoreCore.js';   // [12.0.52] одна шкала
import * as _samМатМодуль from '../ai/samMaskMath.js';   // [12.0.52] проверки окна и среза MedSAM
import { showToast } from '../utils/helpers.js';
import { cornerstone } from '../core/init.js';
// [7.0.37] clinicalWorker.js удалён как мёртвый код (_tryWorker нигде не вызывался).
// Весь клинический счёт идёт на главном потоке: кальций — aiModule.calciumScoring,
// остальное — инлайн в этом файле.
import { log } from '../utils/logger.js';
import { bmdCortex } from './bmdCortexCore.js';   // [7.0.38] BMD по Pickhardt (позвонок по ободку)

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();



// ══════════════════════════════════════════════════════════════
//  HELPERS: доступ к данным объёма
// ══════════════════════════════════════════════════════════════

/**
 * Данные тома для клинических расчётов. [12.0.52] Переписано.
 *
 * Прежде: первый актёр первого окна, у которого есть скаляры. У объёмных
 * окон Cornerstone 5.6 скаляров на актёре нет, у СТЕКОВЫХ есть — это один
 * текущий снимок [w, h, 1]. В раскладке «2D Стопка» расчёты шли по одному
 * срезу и подавались как объём; без volumeId (CPR, экспорт NIfTI) брался
 * «первый том кэша» — бывало, чужая серия или маска. Выбор теперь в
 * core/volumePick.js: поимённо → активное окно MPR → любое объёмное окно;
 * маски пропускаются; не нашли — null и причина в последнийОтказТома().
 *
 * @param {string} [volumeId] — том исследования
 * @returns {{scalarData, dims, spacing, origin, direction, imageData, modality}|null}
 */
let _последнийОтказТома = '';
export function последнийОтказТома() { return _последнийОтказТома; }

export function getVolumeData(volumeId) {
  _последнийОтказТома = '';
  try {
    const re = getRenderingEngine();
    const ids = getViewportIds();
    const активное = getActiveOrFirstViewportId();
    const окна = [активное, ...ids.filter((id) => id !== активное)]
      .map((id) => { try { return re?.getViewport?.(id); } catch (e) { return null; } })
      .filter(Boolean);
    const н = найтиТом(cornerstone?.cache, volumeId, окна);
    if (!н.ok) { _последнийОтказТома = н.причина; log.warn('[ClinicalTools] том не найден:', н.причина); return null; }
    const д = данныеТома(н.том);
    if (!д) {
      _последнийОтказТома = 'объём ещё загружается — дождитесь конца загрузки серии';
      log.warn('[ClinicalTools] данные тома не собраны:', н.id);
      return null;
    }
    log.debug(`[ClinicalTools] том ${н.id} (${н.откуда}), ${д.dims.join('×')}`);
    return д;
  } catch (e) {
    _последнийОтказТома = 'не удалось прочитать объём: ' + e.message;
    log.warn('[ClinicalTools] getVolumeData:', e.message);
    return null;
  }
}

/** Тост об отказе выбора тома — одной строкой для всех кнопок. */
function _нетТома(что) {
  showToast(`${что}: ${_последнийОтказТома || 'объём не загружен'}`, 'warning', 6000);
}

// [12.0.52] getSlicePixelData удалена: после 11.1.56 её не звал никто (MedSAM
// берёт срез сам, _samСрезИзОкна), а внутри она брала срез по
// getCurrentImageIdIndex, который у окна с нормалью [0,0,−1] указывает на
// зеркальный срез (см. [11.1.55] ниже). Мёртвый код с ловушкой — убран.

// ══════════════════════════════════════════════════════════════
//  РЕЗУЛЬТАТЫ — модальное окно
// ══════════════════════════════════════════════════════════════

export function showResultModal(title, bodyHtml, options = {}) {
  document.getElementById('clinical-result-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'clinical-result-modal';
  modal.style.cssText = `
    position:fixed; inset:0; z-index:10000; display:flex; align-items:center; justify-content:center;
    background:rgba(0,0,0,0.7); backdrop-filter:blur(4px);
  `;
  modal.innerHTML = `
    <div style="background:#1a1f2e; border:1px solid #334155; border-radius:12px; max-width:600px; width:90%;
                max-height:80vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,0.5);">
      <div style="display:flex; justify-content:space-between; align-items:center; padding:16px 20px;
                  border-bottom:1px solid #334155;">
        <h3 style="margin:0; color:#fff; font-size:1.1rem;">${title}</h3>
        <button id="clinical-modal-close" style="background:none; border:none; color:#888; font-size:1.4rem;
                cursor:pointer; padding:0 4px;">&times;</button>
      </div>
      <div style="padding:20px; color:#c5c6c7; font-size:0.9rem; line-height:1.7;">
        ${bodyHtml}
      </div>
      ${options.footer ? `<div style="padding:12px 20px; border-top:1px solid #334155; text-align:right;">${options.footer}</div>` : ''}
    </div>
  `;
  document.body.appendChild(modal);

  modal.querySelector('#clinical-modal-close').onclick = () => modal.remove();
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); }, { signal: _ac.signal });
}

// Стили для таблиц результатов
const TABLE_STYLE = `
  width:100%; border-collapse:collapse; margin:10px 0;
`;
const TH_STYLE = `
  text-align:left; padding:8px 12px; background:rgba(255,255,255,0.05);
  color:#66fcf1; font-weight:600; border-bottom:1px solid #334155;
`;
const TD_STYLE = `
  padding:8px 12px; border-bottom:1px solid rgba(255,255,255,0.05);
`;

function riskColor(level) {
  return['#22c55e','#84cc16','#eab308','#f97316','#ef4444'][level] || '#888';
}


// ══════════════════════════════════════════════════════════════
//  1. AGATSTON CALCIUM SCORING
// ══════════════════════════════════════════════════════════════

/**
 * Перейти всеми окнами MPR к точке тома (индексы вокселя).
 * [12.0.52] Для кнопок «показать» в окнах результата: врачу нужно увидеть
 * очаг или срез, а не номер в чужой нумерации.
 */
async function _показатьТочку(vd, ijk) {
  try {
    const { jumpToWorldPosition } = await import('../core/viewport.js');
    jumpToWorldPosition(индексВМир(vd, ijk));
  } catch (e) { log.warn('[Клинические] переход к точке:', e.message); }
}

/**
 * [12.0.52] Индекс Агатстона: счёт — ai/aiModule.calciumScoring (единый для
 * всех кнопок; лёгкие, область сердца, отказ при контрасте, отсев костей —
 * ai/calciumScoreCore.кальцийГруди). Здесь — окно со СПИСКОМ очагов: каждый
 * можно показать на снимке и снять галочкой; сумма пересчитывается по
 * отмеченным. Так устроены и программы для кальциевого индекса: машина
 * находит кальцинаты, врач решает, какие из них коронарные.
 *
 * Прежде здесь: «проверка» максимума по первому миллиону вокселей (около
 * четырёх срезов) и, если там не было 130 HU, — окно «0, кальций не
 * обнаружен, риск минимальный» без всякого счёта; подпись «ACC/AHA 2010»
 * у ступеней, которых в том документе нет; счёт по прямоугольнику с
 * позвоночником (на синтетике 218 425 вместо 775).
 */
export async function runAgatstonScore(volumeId) {
  try {
    const vd = getVolumeData(volumeId);
    if (!vd) { _нетТома('Индекс Агатстона'); return; }
    const неКТ = требуетсяКТ(vd, 'Индекс Агатстона');
    if (неКТ) { showToast(неКТ, 'warning', 6000); return; }
    showToast('Индекс Агатстона: ищу лёгкие, сердце и кальцинаты…', 'info', 3000);

    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
    const р = await calciumScoring(scalarData, nx, ny, nz, spacing, { direction: vd.direction });
    if (!р?.ok) {
      showResultModal('Индекс Агатстона', `
        <div style="color:#eab308; font-weight:600; margin-bottom:10px;">Не посчитан</div>
        <div>${р?.отказ || 'причина неизвестна'}</div>`);
      return;
    }

    const очаги = р.lesions || [];
    const строки = очаги.map((l, n) => `<tr>
        <td style="${TD_STYLE}"><input type="checkbox" class="аг-очаг" data-n="${n}" checked></td>
        <td style="${TD_STYLE}">${n + 1}</td>
        <td style="${TD_STYLE}">${Math.round(l.scoreExact ?? l.score)}</td>
        <td style="${TD_STYLE}">${Math.round(l.maxHU)}</td>
        <td style="${TD_STYLE}">${l.volumeMm3.toFixed(1)} мм³</td>
        <td style="${TD_STYLE}"><button class="аг-показать" data-n="${n}" style="padding:2px 8px; cursor:pointer;">показать</button></td>
      </tr>`).join('');
    const толщина = Number(spacing[2]) || 0;
    const проТолщину = Math.abs(толщина - 3) > 0.25
      ? `<div style="color:#eab308;">Шаг срезов ${толщина.toFixed(1)} мм. Индекс определён для срезов 3 мм; счёт пересчитан на 3 мм (× ${(толщина / 3).toFixed(2)}), но сравнивать с нормами корректно только по реконструкции 3 мм.</div>`
      : '';

    showResultModal('Индекс Агатстона', `
      <div style="text-align:center; margin-bottom:14px;">
        <div id="аг-итог" style="font-size:2.6rem; font-weight:700;"></div>
        <div id="аг-категория" style="font-size:1rem;"></div>
        <div style="color:#888; font-size:0.8rem;">сумма по отмеченным очагам</div>
      </div>
      <div style="padding:10px; background:rgba(234,179,8,0.08); border:1px solid rgba(234,179,8,0.35); border-radius:6px; font-size:0.85rem; margin-bottom:10px;">
        Найдены все кальцинаты в области сердца и средостения — <b>не только коронарные</b>: кальциноз
        клапана, кольца митрального клапана, стенки аорты, хрящей у грудины тоже попадают в список.
        Нажмите «показать» у каждого очага и <b>снимите галочку</b> с некоронарных — сумма пересчитается.
      </div>
      ${очаги.length ? `<table style="${TABLE_STYLE}">
        <tr><th style="${TH_STYLE}"></th><th style="${TH_STYLE}">#</th><th style="${TH_STYLE}">Баллы</th>
            <th style="${TH_STYLE}">Пик HU</th><th style="${TH_STYLE}">Объём</th><th style="${TH_STYLE}"></th></tr>
        ${строки}
      </table>` : '<div style="text-align:center; color:#22c55e;">Кальцинатов ≥130 HU в области сердца не найдено</div>'}
      <div style="margin-top:12px; padding:10px; background:rgba(255,255,255,0.03); border-radius:6px; font-size:0.8rem; color:#888; line-height:1.6;">
        Метод: Agatston AS et al., JACC 1990 — порог 130 HU, площадь очага на срезе × вес по пику (1: 130–199, 2: 200–299, 3: 300–399, 4: ≥400).
        Ступени: 0 — нет; 1–10 — минимальный; 11–100 — лёгкий; 101–400 — умеренный; >400 — выраженный кальциноз
        (Rumberger JA et al., Mayo Clin Proc 1999; по памяти). Это количество кальция, а не риск пациента: риск зависит от возраста, пола и факторов риска.<br>
        Нужна бесконтрастная КТ с ЭКГ-синхронизацией; синхронизацию программа проверить не может.
        Кости (позвонки, грудина, рёбра) отсеяны по размеру: ${р.исключеноКрупных} крупн. кусков.
        ${проТолщину}
        Объём: ${nx}×${ny}×${nz}, шаг ${spacing.map((s) => Number(s).toFixed(2)).join('×')} мм.
      </div>
    `);

    const окно = document.getElementById('clinical-result-modal');
    const пересчитать = () => {
      let сумма = 0;
      окно?.querySelectorAll('.аг-очаг').forEach((ч) => { if (ч.checked) сумма += очаги[+ч.dataset.n].scoreExact ?? очаги[+ч.dataset.n].score; });
      const итог = Math.round(сумма);
      const цвет = riskColor(уровеньАгатстона(итог));
      const эИтог = окно?.querySelector('#аг-итог'), эКат = окно?.querySelector('#аг-категория');
      if (эИтог) { эИтог.textContent = String(итог); эИтог.style.color = цвет; }
      if (эКат) { эКат.textContent = категорияАгатстона(итог); эКат.style.color = цвет; }
    };
    окно?.querySelectorAll('.аг-очаг').forEach((ч) => ч.addEventListener('change', пересчитать));
    окно?.querySelectorAll('.аг-показать').forEach((к) => к.addEventListener('click', () => {
      const c = очаги[+к.dataset.n].centroid;
      _показатьТочку(vd, [c.x, c.y, c.z]);
    }));
    пересчитать();
    showToast(`Индекс Агатстона: найдено очагов ${очаги.length} — проверьте список`, 'info', 4000);
  } catch (e) {
    log.warn('[clinicalTools] runAgatstonScore failed:', e.message);
    showToast('Индекс Агатстона: ' + e.message, 'error', 5000);
  }
}


// ══════════════════════════════════════════════════════════════
//  2. ЭМФИЗЕМА — количественный анализ
// ══════════════════════════════════════════════════════════════

/**
 * [12.0.52] Денситометрия по МАСКЕ ЛЁГКИХ (core/thoraxCore.js).
 *
 * Прежде считались все воксели −1000…−200 HU во всём томе: воздух вокруг
 * пациента в поле, газ кишки, трахея. На синтетической груди без эмфиземы
 * выходило 84% «Тяжёлая» при «объёме лёгких» 14,5 л. Главным порогом стоял
 * −910 HU с подписью «стандартный» — он из работы 1988 г. на срезах 10 мм;
 * для тонких срезов принят −950 HU. Градации «лёгкая/умеренная/тяжёлая»
 * (5/15/25%) источника не имели — убраны.
 */
export async function runEmphysemaQuantification(volumeId) {
  try {
    const vd = getVolumeData(volumeId);
    if (!vd) { _нетТома('Эмфизема'); return; }
    const неКТ = требуетсяКТ(vd, 'Денситометрия лёгких');
    if (неКТ) { showToast(неКТ, 'warning', 6000); return; }
    showToast('Эмфизема: выделяю лёгкие…', 'info', 3000);
    await new Promise((r) => setTimeout(r, 30));

    const { сегментироватьЛёгкие, эмфизема } = await import('../core/thoraxCore.js');
    const лёгкие = сегментироватьЛёгкие(vd.scalarData, vd.dims, vd.spacing, { direction: vd.direction });
    const р = эмфизема(vd.scalarData, vd.dims, vd.spacing, лёгкие, { direction: vd.direction });
    if (!р.ok) {
      showResultModal('Денситометрия лёгких', `<div style="color:#eab308;">Не посчитано: ${р.причина}</div>`);
      return;
    }
    const выше = р.laa950 >= 5;
    showResultModal('Денситометрия лёгких', `
      <div style="text-align:center; margin-bottom:16px;">
        <div style="font-size:2.4rem; font-weight:700;">${р.laa950}%</div>
        <div style="color:#9fb3cf;">доля лёгочной ткани ниже −950 HU (LAA−950)</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">LAA−950, правое лёгкое</td><td style="${TD_STYLE}">${р.правое.laa950 ?? '—'}% (${р.правое.мл} мл)</td></tr>
        <tr><td style="${TD_STYLE}">LAA−950, левое лёгкое</td><td style="${TD_STYLE}">${р.левое.laa950 ?? '—'}% (${р.левое.мл} мл)</td></tr>
        <tr><td style="${TD_STYLE}">P15 (15-й перцентиль плотности)</td><td style="${TD_STYLE}">${р.p15} HU</td></tr>
        <tr><td style="${TD_STYLE}">Объём лёгких</td><td style="${TD_STYLE}">${р.объёмМл} мл</td></tr>
        <tr><td style="${TD_STYLE}">Средняя плотность</td><td style="${TD_STYLE}">${р.средняяHU} HU</td></tr>
        <tr><td style="${TD_STYLE}">Трахея и главные бронхи</td><td style="${TD_STYLE}">${р.путиВычтены ? 'вычтены' : 'НЕ вычтены (см. ниже)'}</td></tr>
      </table>
      ${р.предупреждения.length ? `<div style="margin-top:10px; color:#eab308; font-size:0.85rem;">${р.предупреждения.map((т) => '• ' + т).join('<br>')}</div>` : ''}
      <div style="margin-top:12px; padding:10px; background:rgba(255,255,255,0.03); border-radius:6px; font-size:0.8rem; color:#888; line-height:1.6;">
        Ориентир (по памяти): в исследовании COPDGene эмфиземой считают LAA−950 ≥ 5% на вдохе —
        здесь ${выше ? '<b>выше</b>' : 'ниже'} этой границы. Порог −950 HU — Gevenois PA et al., 1996; P15 — Dirksen A et al., 1999.<br>
        Значения верны для КТ на глубоком вдохе, без контраста, тонких срезов (≤ 1,5 мм) и «мягкого» ядра реконструкции;
        иначе доля эмфиземы смещается. Маска лёгких — автоматическая (порог −400 HU, сосуды внутри лёгкого включены).
      </div>
    `);
    showToast(`Эмфизема: LAA−950 = ${р.laa950}%`, 'success', 3000);
  } catch (e) {
    log.warn('[clinicalTools] runEmphysemaQuantification failed:', e.message);
    showToast('Эмфизема: ' + e.message, 'error', 5000);
  }
}


// ══════════════════════════════════════════════════════════════
//  3. CTR — Кардиоторакальный индекс
// ══════════════════════════════════════════════════════════════

/**
 * [12.0.52] КТИ по лёгким (core/thoraxCore.кти): сердце — щель между
 * медиальными краями лёгких, грудь — между наружными краями.
 *
 * Прежде «сердцем» считалось всё 0…500 HU в полосе 30–80% кадра — мышцы
 * груди, печень, позвоночник, — а «грудью» — тело целиком по коже. На
 * синтетике с сердцем 97,5 мм выходило 0,956 «кардиомегалия»; при сердце
 * 152,5 мм — те же 0,956: ответ от сердца не зависел.
 */
export async function runCTR(volumeId) {
  try {
    const vd = getVolumeData(volumeId);
    if (!vd) { _нетТома('КТИ'); return; }
    const неКТ = требуетсяКТ(vd, 'КТИ');
    if (неКТ) { showToast(неКТ, 'warning', 6000); return; }
    showToast('КТИ: выделяю лёгкие…', 'info', 3000);
    await new Promise((r) => setTimeout(r, 30));

    const { сегментироватьЛёгкие, кти } = await import('../core/thoraxCore.js');
    const лёгкие = сегментироватьЛёгкие(vd.scalarData, vd.dims, vd.spacing, { direction: vd.direction });
    const р = кти(vd.scalarData, vd.dims, vd.spacing, лёгкие, { direction: vd.direction });
    if (!р.ok) {
      showResultModal('Кардиоторакальный индекс', `<div style="color:#eab308;">Не посчитан: ${р.причина}</div>`);
      return;
    }
    const [nx, ny] = vd.dims;
    showResultModal('Кардиоторакальный индекс (осевая КТ)', `
      <div style="text-align:center; margin-bottom:16px;">
        <div style="font-size:2.6rem; font-weight:700;">${р.кти.toFixed(2)}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Поперечник сердца</td><td style="${TD_STYLE}">${р.сердцеМм} мм
          <button id="кти-сердце" style="margin-left:8px; padding:2px 8px; cursor:pointer;">показать срез</button></td></tr>
        <tr><td style="${TD_STYLE}">Внутренний поперечник груди</td><td style="${TD_STYLE}">${р.грудьМм} мм
          <button id="кти-грудь" style="margin-left:8px; padding:2px 8px; cursor:pointer;">показать срез</button></td></tr>
      </table>
      <div style="margin-top:12px; padding:10px; background:rgba(255,255,255,0.03); border-radius:6px; font-size:0.8rem; color:#888; line-height:1.6;">
        Сердце — наибольшее расстояние по горизонтали между лёгкими на средних срезах (перикард и жир около сердца входят);
        грудь — наибольшее расстояние между наружными краями лёгких. Проверьте оба среза глазами.<br>
        Порог 0,50 — рентгенографический (снимок стоя, на вдохе, Danzer 1919; по памяти). Для осевой КТ лёжа он не выверен —
        число ориентир, а не диагноз «кардиомегалия».
      </div>
    `);
    const окно = document.getElementById('clinical-result-modal');
    окно?.querySelector('#кти-сердце')?.addEventListener('click', () => _показатьТочку(vd, [nx / 2, ny / 2, р.zСердца]));
    окно?.querySelector('#кти-грудь')?.addEventListener('click', () => _показатьТочку(vd, [nx / 2, ny / 2, р.zГруди]));
    showToast(`КТИ: ${р.кти.toFixed(2)}`, 'success', 3000);
  } catch (e) {
    log.warn('[clinicalTools] runCTR failed:', e.message);
    showToast('КТИ: ' + e.message, 'error', 5000);
  }
}


// ══════════════════════════════════════════════════════════════
//  4. BMD — Костная минеральная плотность
// ══════════════════════════════════════════════════════════════

export async function runBMD(volumeId) {
  try {
    const vd = getVolumeData(volumeId);
    if (!vd) { _нетТома('BMD'); return; }
    // [12.0.52] Плотность в HU есть только у КТ: на МРТ «HU позвонка» —
    // условные единицы яркости, пороги Pickhardt к ним неприменимы.
    const неКТ = требуетсяКТ(vd, 'BMD');
    if (неКТ) { showToast(неКТ, 'warning', 6000); return; }
    // [12.0.52] Рамка поиска позвонка задана в долях кадра: спина — внизу
    // кадра (рост y — к спине), голова — к концу тома. Так у обычной осевой
    // КТ на спине; при иной укладке рамка легла бы не туда — отказ, а не
    // «число из ниоткуда». Конец тома к голове или к ногам — читаем из
    // направления осей и разворачиваем полосу срезов.
    const D = vd.direction || [1, 0, 0, 0, 1, 0, 0, 0, 1];
    if (!(Math.abs(D[0]) > 0.9 && D[4] > 0.9)) {
      showToast('BMD: серия не в обычной осевой ориентации (или пациент не на спине) — автоматический поиск позвонка неприменим, поставьте ROI вручную', 'warning', 8000);
      return;
    }
    const кГолове = !(D[8] < 0);
    showToast('BMD: ищу тело позвонка…', 'info', 2500);

    // [7.0.38] Позвонок ищется по кортикальному ободку, дальше средний HU губчатой,
    // пороги Pickhardt 160/110. До этого стоял фиксированный квадратик в середине
    // кадра на высоте 0.7·H, и всё в окне 50–400 HU считалось костью. Он не знал,
    // что под ним: на однородных тканях 60 HU выдавал «Остеопороз», а у пациента на
    // животе (КТ-колонография — ровно та популяция скрининга) попадал на контрастную
    // аорту ~300 HU и писал «Норма» при позвонке 90 HU. Ободок в таких случаях
    // честно отказывается — см. tests/bmd_test.mjs, разделы 6 и 7.
    const r = bmdCortex(vd.scalarData, vd.dims, кГолове ? {} : { zStart: 0.15, zEnd: 0.45 });

    if (r.trabecularHU === null) {
      showToast('⚠️ Тело позвонка не локализовано', 'warning');
      showResultModal('💪 Костная минеральная плотность (BMD)', `
        <div style="text-align:center; margin-bottom:16px; color:#888; font-weight:600;">${r.category}</div>
        <div style="font-size:0.85rem; color:#888;">${r.note}</div>
      `);
      return;
    }

    const hu = r.trabecularHU;
    showResultModal('💪 Костная минеральная плотность (BMD)', `
      <div style="text-align:center; margin-bottom:20px;">
        <div style="font-size:2.5rem; font-weight:700; color:${r.catColor};">${hu.toFixed(0)} HU</div>
        <div style="color:${r.catColor};">${r.category}</div>
      </div>
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Средний HU губчатой кости</td><td style="${TD_STYLE}"><strong>${hu.toFixed(1)} HU</strong></td></tr>
        <tr><td style="${TD_STYLE}">Диапазон по срезам</td><td style="${TD_STYLE}">${r.minHU.toFixed(0)} — ${r.maxHU.toFixed(0)} HU</td></tr>
        <tr><td style="${TD_STYLE}">Срезов с позвонком</td><td style="${TD_STYLE}">${r.slices}</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей губчатой</td><td style="${TD_STYLE}">${r.trabVoxels.toLocaleString()}</td></tr>
      </table>
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Оппортунистический скрининг BMD по КТ (Pickhardt 2013): тело позвонка по кортикальному ободку,
        средний HU губчатой кости.<br>
        &gt;160 HU = норма, 110–160 = остеопения (погранично), &lt;110 = подозрение на остеопороз.<br>
        Только нативная фаза. Авто-ROI приблизительный — подтвердить DXA/QCT.<br>
        Позвонки берутся на 15–45% длины серии от её головного конца; КАКИЕ это позвонки,
        программа не определяет. Пороги Pickhardt выведены для L1 — на грудных позвонках плотность выше,
        и «норма» там может скрыть остеопению. Проверьте уровень глазами.<!-- [12.0.52] было «(L-позвонки)» без проверки уровня -->
      </div>
    `);

    showToast(`💪 BMD: ${hu.toFixed(0)} HU — ${r.category}`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runBMD failed:`, e.message);  }
}


// ══════════════════════════════════════════════════════════════
//  5. [12.0.52] УДАЛЕНО: «Пневмоторакс» (runPneumothoraxDetection)
// ══════════════════════════════════════════════════════════════
//
// Кнопка сравнивала долю вокселей < −900 HU в левой и правой половине КАДРА
// (а не лёгких): в счёт шёл воздух вокруг пациента, трахея, газ желудка.
// Разница половин ≥ 3% — «Возможный пневмоторакс», ≥ 10% — «Вероятный».
// Замер на синтетической груди БЕЗ пневмоторакса: «Возможный пневмоторакс
// (Правая)» — асимметрия 6% от одного положения стола и сердца.
//
// Хуже: сторона была ПЕРЕПУТАНА. Малый x назывался «Левая», а в DICOM (LPS)
// малый x — правый бок пациента. Ошибку уже исправляли — но в КОПИИ для
// проверок (clinicalInlineCore.js), и тест копии был зелёным, а кнопка
// продолжала путать стороны. Ровно та ловушка, о которой предупреждал
// mirrors_test.
//
// «Нет признаков пневмоторакса» при асимметрии < 3% — это ложное
// успокоение: двусторонний или небольшой передний пневмоторакс асимметрии
// не даёт. Детектор, который пропускает и выдумывает, хуже его отсутствия.
// Честная замена потребовала бы найти плевральную щель — воздух между
// лёгким и грудной стенкой, — это отдельная задача.


// ══════════════════════════════════════════════════════════════
//  6. КАМЕРЫ СЕРДЦА + EF
// ══════════════════════════════════════════════════════════════

export async function runCardiacSegmentation(volumeId) {
  try {
    showToast('❤️ Сегментация камер сердца...', 'info');
  
    const vd = getVolumeData(volumeId);
    if (!vd) { showToast('⚠️ Загрузите КТ объём', 'warning'); return; }
  
    const { scalarData, dims, spacing } = vd;
    const [nx, ny, nz] = dims;
  
    const result = await segmentCardiacChambers(scalarData, nx, ny, nz, spacing);
    if (!result) { showToast('❌ Сегментация не удалась', 'error', 2000); return; }
  
    const ch = result.chambers;
    let chambersHtml = '';
    const chamberNames = { LV: 'Левый желудочек', RV: 'Правый желудочек', LA: 'Левое предсердие', RA: 'Правое предсердие' };
    const chamberColors = { LV: '#ef4444', RV: '#3b82f6', LA: '#f97316', RA: '#22c55e' };
  
    for (const [key, name] of Object.entries(chamberNames)) {
      const c = ch[key];
      if (c) {
        chambersHtml += `
          <tr>
            <td style="${TD_STYLE}"><span style="color:${chamberColors[key]};">●</span> ${name}</td>
            <td style="${TD_STYLE}">${c.volumeMl} мл</td>
            <td style="${TD_STYLE}">${c.voxels.toLocaleString()}</td>
          </tr>
        `;
      }
    }
  
    let efHtml = '';
    if (ch.LV) {
      const ef = estimateEF(ch.LV.volumeMl);
      efHtml = ef.ef !== null
        ? `<div style="margin-top:15px; text-align:center;">
             <div style="font-size:1.5rem; color:#66fcf1;">EF ≈ ${ef.ef}%</div>
             <div style="font-size:0.8rem; color:#888;">Метод: ${ef.method}</div>
           </div>`
        : `<div style="margin-top:15px; color:#888; text-align:center; font-size:0.85rem;">
             EF: требуется двухфазное сканирование (ED + ES)
           </div>`;
    }
  
    showResultModal('❤️ Сегментация камер сердца', `
      <div style="text-align:center; margin-bottom:15px;">
        <span style="font-size:0.85rem; color:#888;">Контраст: ${result.hasContrast ? '✅ Да' : '❌ Нет'}</span>
      </div>
      ${chambersHtml ? `
        <table style="${TABLE_STYLE}">
          <tr>
            <th style="${TH_STYLE}">Камера</th>
            <th style="${TH_STYLE}">Объём</th>
            <th style="${TH_STYLE}">Вокселей</th>
          </tr>
          ${chambersHtml}
        </table>
      ` : '<div style="text-align:center; color:#888; padding:20px;">Камеры не обнаружены. Для точной сегментации нужна КТ с контрастированием.</div>'}
      ${efHtml}
      <div style="margin-top:12px; font-size:0.8rem; color:#888;">
        Пороговая сегментация: кровь ${result.hasContrast ? '150-500' : '25-90'} HU + морфология.<br>
        Для клинической оценки EF используйте двухфазный КТ-протокол.
      </div>
    `);
  } catch (e) { log.warn(`[clinicalTools] runCardiacSegmentation failed:`, e.message); showToast('❌ Камеры: ' + e.message, 'error', 3000); }
}


// ══════════════════════════════════════════════════════════════
//  7. REGION GROW 3D — Заливка от точки
// ══════════════════════════════════════════════════════════════

/**
 * [11.1.38] Семя для роста области — центр тяжести нарисованного сегмента.
 * Врач ставит кистью точку внутри органа; это его «ткнуть мышью», только
 * работающее в любом срезовом окне и переживающее смену раскладки.
 *
 * Почему центр тяжести, а не первый воксель: врач чаще обводит КОНТУР, и
 * первый воксель кольца лежит на границе органа — рост оттуда сразу
 * утечёт в соседа. Центр тяжести кольца лежит внутри органа.
 *
 * @returns {Promise<number|null>} линейный индекс вокселя или null, если метки нет
 */
async function _семяИзМаски(dims, индекс = 1) {
  try {
    const { томМаски } = await import('../core/organView.js');
    const м = томМаски();
    if (!м || м.ошибка || !м.пиксели?.length) return null;
    const [nx, ny] = dims;
    let сумма = 0, сx = 0, сy = 0, сz = 0;
    for (let i = 0; i < м.пиксели.length; i++) {
      if (м.пиксели[i] !== индекс) continue;
      const z = Math.floor(i / (nx * ny));
      const y = Math.floor((i % (nx * ny)) / nx);
      const x = i % nx;
      сx += x; сy += y; сz += z; сумма++;
    }
    if (!сумма) return null;
    const x = Math.round(сx / сумма), y = Math.round(сy / сумма), z = Math.round(сz / сумма);
    const idx = z * nx * ny + y * nx + x;
    return (idx >= 0 && idx < м.пиксели.length) ? idx : null;
  } catch (e) { return null; }
}

export async function runRegionGrow3D(volumeId, options = {}) {
  try {
    // [11.1.38.1] ТОМ БЕРЁТСЯ ПОД РАЗМЕР МАСКИ, а не «первый попавшийся».
    //
    // Владелец поймал на живом: «Seed HU undefined», «Диапазон NaN — NaN»,
    // «В маску не записано: размер маски не совпал с объёмом». Причина:
    // getVolumeData идёт через ПЕРВОГО актёра окна, а с появлением
    // сегментации первым актёром в срезовом окне вполне может оказаться
    // САМА МАСКА (labelmap-наложение) или иной том другой сетки. Семя тогда
    // считается по одним размерам, плотность читается из других — отсюда
    // undefined и NaN, а результат некуда записать.
    //
    // Теперь: сначала маска (она — адресат результата), затем ищем том с
    // ТОЙ ЖЕ длиной массива, начиная с поимённого volumeId и далее по кешу,
    // пропуская сами маски. Совпадение длины — единственная надёжная
    // сверка сеток, доступная без метаданных.
    const { томМаски: _томМаски } = await import('../core/organView.js');
    const { этоПолурешётка, huПрямое, huСреднее2х2х2 } = await import('../core/maskGrid.js');
    const { ростОбласти } = await import('../core/regionGrow.js');
    const маскаДляРоста = _томМаски();
    let vd = getVolumeData(volumeId);
    // [12.0.7] Маска ПОЛОВИННОГО шага узнаётся ДО охоты по длине: у неё длины
    // с томом не совпадают ПО ПОСТРОЕНИЮ, и прежняя охота «том той же длины»
    // подобрала бы что попало. Признак — ceil-половина размеров по каждой оси.
    let полурешёткаРоста = false;
    if (маскаДляРоста?.dims && vd?.dims && этоПолурешётка(маскаДляРоста.dims, vd.dims)) {
      полурешёткаРоста = true;
    } else if (маскаДляРоста?.dims) {
      // Возможно, первым пришёл не тот том — поищем в кеше том, для которого
      // маска есть полурешётка.
      try {
        for (const v of (cornerstone?.cache?.getVolumes?.() || [])) {
          if (/labelmap|mikhmel-seg/i.test(String(v?.volumeId || ''))) continue;
          if (v?.dimensions && этоПолурешётка(маскаДляРоста.dims, v.dimensions)) {
            const д = v?.voxelManager?.getCompleteScalarDataArray?.() || v?.getScalarData?.();
            if (д?.length) {
              vd = { scalarData: д, dims: v.dimensions, spacing: v.spacing || [1, 1, 1], origin: v.origin };
              полурешёткаРоста = true;
              log.info(`[RegionGrow] том подобран как МЕЛКАЯ сетка полурешётки: ${v.volumeId}`);
              break;
            }
          }
        }
      } catch (eк) { /* кеш недоступен */ }
    }
    const нужнаДлина = маскаДляРоста?.пиксели?.length || 0;
    if (!полурешёткаРоста && нужнаДлина && vd?.scalarData?.length !== нужнаДлина) {
      const кэш = cornerstone?.cache;
      const кандидаты = [];
      try {
        const прям = кэш?.getVolume?.(String(volumeId).startsWith('cornerstoneStreamingImageVolume:')
          ? volumeId : `cornerstoneStreamingImageVolume:${volumeId}`);
        if (прям) кандидаты.push(прям);
        for (const v of (кэш?.getVolumes?.() || [])) {
          if (!/labelmap|mikhmel-seg/i.test(String(v?.volumeId || ''))) кандидаты.push(v);
        }
      } catch (eк) { /* кеш недоступен — останемся на vd */ }
      for (const v of кандидаты) {
        const д = v?.voxelManager?.getCompleteScalarDataArray?.() || v?.getScalarData?.();
        if (д?.length === нужнаДлина) {
          vd = { scalarData: д, dims: v.dimensions, spacing: v.spacing || [1, 1, 1], origin: v.origin };
          log.info(`[RegionGrow] том подобран под размер маски: ${v.volumeId}`);
          break;
        }
      }
    }
    if (!vd?.scalarData?.length) { showToast('⚠️ Загрузите объём', 'warning'); return; }

    const { scalarData } = vd;
    // [12.0.7] РОСТ ИДЁТ ПО СЕТКЕ МАСКИ (она — адресат результата). На общей
    // сетке это та же сетка тома; на полурешётке — её собственная, а плотность
    // приходит средним мелкой восьмёрки (см. maskGrid, промерено фантомами).
    const dims = полурешёткаРоста ? маскаДляРоста.dims : vd.dims;
    const spacing = полурешёткаРоста ? (маскаДляРоста.spacing || [1, 1, 1]) : vd.spacing;
    const huПоТочке = полурешёткаРоста ? huСреднее2х2х2(scalarData, vd.dims) : huПрямое(scalarData, vd.dims);
    const[nx, ny, nz] = dims;
    const range = options.range || 100; 

    // [11.1.38] СЕМЯ БЕРЁТСЯ ИЗ МАСКИ ВРАЧА, А НЕ ИЗ ЦЕНТРА ОБЪЁМА.
    //
    // Здесь стояло seedX/Y/Z = середина объёма — жёстко, без единого вопроса
    // к человеку. Владелец справедливо написал: «она даже ткнуть никуда не
    // даёт, сразу выводит модальное окно». Центр КТ живота — это просвет
    // кишки или позвоночник, а не орган; отсюда и его отчёт «Seed HU 14,
    // Seed координаты [256, 256, 0], 0.0 мл» — программа растила пузырь
    // мягких тканей от случайной точки и честно сообщала ерунду. Ещё и
    // seedZ вышел 0: dims приходили от ПЕРВОГО актёра первого окна, а им
    // мог оказаться не том исследования.
    //
    // Теперь: том берётся ПОИМЁННО по volumeId, а семя — центр тяжести уже
    // нарисованного сегмента. То есть врач ставит кистью точку (или мазок)
    // внутри органа — это и есть его «ткнуть», причём в любом из срезовых
    // окон. Нет мазка — честный отказ словами, а не выдуманная точка.
    const семя = options.seedIndex ?? await _семяИзМаски(dims, options.segIndex || 1);
    if (семя != null && (семя < 0 || семя >= nx * ny * nz)) {
      showToast('Метка кистью не ложится на сетку объёма — сообщите об этом: сетки маски и снимка разошлись.', 'error', 6000);
      return;
    }
    if (семя == null) {
      showToast('Ткните кистью внутрь органа (одной точки хватит) и нажмите снова: рост области идёт ОТ вашей метки, выдумывать её за вас я не буду.', 'warning', 7000);
      return;
    }
    const seedIdx = семя;
    const seedZ = Math.floor(seedIdx / (nx * ny));
    const seedY = Math.floor((seedIdx % (nx * ny)) / nx);
    const seedX = seedIdx % nx;
    const seedHU = huПоТочке(seedX, seedY, seedZ);
  
    showToast(`📦 Наращивание области: затравка=${Math.round(seedHU)} HU, диапазон ±${range}...`, 'info');
  
    const low = seedHU - range;
    const high = seedHU + range;
    const MAX_VOXELS = 5_000_000; 

    // [11.1.43] РОСТ ЗАПЕРТ В САМОЙ РАЗМЕТКЕ, РАЗДУТОЙ НА ЗАПАС.
    //
    // Прямоугольная рамка (11.1.41) была слишком щедрой: владелец обводил
    // селезёнку в трёх проекциях, ящик растянулся на 150×242×150 вокселей, и
    // внутри него оказались печень и мышцы спины — рост заполнил 70% ящика
    // (1 617 мл, скриншот). Ящик вокруг органа не есть орган.
    //
    // Теперь разрешённая зона повторяет ФОРМУ разметки: расти можно только
    // туда, куда можно дойти от обводок за 10 мм. Печень за пределами органа
    // недостижима, а добрать неразмеченную середину — можно.
    let зона = null;
    try {
      const { зонаРоста } = await import('../core/maskMorph.js');
      const мм = 10;
      const шагов = Math.max(2, Math.round(мм / Math.min(spacing[0] || 1, spacing[1] || 1, spacing[2] || 1)));
      зона = зонаРоста(маскаДляРоста?.пиксели, dims, options.segIndex || 1, шагов);
      if (зона) {
        let разрешено = 0;
        for (let i = 0; i < зона.length; i++) if (зона[i]) разрешено++;
        log.info(`[RegionGrow] зона роста = разметка + ${мм} мм (${шагов} шагов): ${разрешено.toLocaleString('ru-RU')} вокселей разрешено из ${зона.length.toLocaleString('ru-RU')}`);
      } else {
        log.warn('[RegionGrow] зоны нет: сегмент пуст или маска не найдена — рост пойдёт по всему объёму');
      }
    } catch (eз) { log.warn('[RegionGrow] зона недоступна:', eз.message); }
    const рамка = зона;   // для отчёта: зона есть — значит рост ограничен

    // [12.0.7] Сам обход вынут в чистое ядро (core/regionGrow.js) и проверен
    // фантомами: поведение прежнее один в один (6-связность, границы диапазона
    // включительно, зона, потолок), очередь — с указателем головы вместо
    // квадратичного shift(). Здесь остаётся только обвязка.
    const итог = ростОбласти({ huПоТочке, dims, spacing, семяIdx: seedIdx, диапазон: range, зона, максВокселей: MAX_VOXELS });
    if (!итог.ok) { showToast('Рост области: ' + итог.причина, 'error', 6000); return; }
    const visited = итог.visited;
    const count = итог.вокселей;
  
    const volMm3 = итог.объёмМл * 1000;
    const volMl = итог.объёмМл.toFixed(1);

    // [11.1.38] РЕЗУЛЬТАТ УХОДИТ В МАСКУ, а не только в табличку. Раньше
    // выросшая область жила одним числом в модальном окне: ни «Только орган
    // в 3D», ни STL, ни экспорт NIfTI её не видели — расчёт был сам по себе.
    let вМаску = '';
    try {
      const { томМаски, записатьМаску, запомнитьПравкуМаски } = await import('../core/organView.js');
      const м = томМаски();
      // [11.1.40] ТРИ ЗАПРЕТА, купленные бедой владельца: рост с диапазоном
      // ±100 протёк по всему животу (5 млн вокселей, обрезано лимитом) и
      // МОЛЧА влился в сегмент, где лежала ручная работа на 617 мл. Отделить
      // утечку от обводок было нечем.
      //
      //  1. Результат, обрезанный ЛИМИТОМ, не пишется вовсе: рост не
      //     остановился сам, значит область заведомо утекла, и её число
      //     бессмысленно. Пишем словами, что делать.
      //  2. В ЗАНЯТЫЙ сегмент не дописываем: берём первый свободный из 1…5
      //     и говорим об этом. Чужую работу смешивать нельзя.
      //  3. Перед записью запоминается ДЕЛЬТА для отмены — кнопка
      //     «↶ Вернуть маску» в панели «Сегментация».
      const занят = (инд) => { for (let i = 0; i < м.пиксели.length; i++) if (м.пиксели[i] === инд) return true; return false; };
      if (count >= MAX_VOXELS) {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}"><b>НЕ записано: рост упёрся в лимит</b> — область не остановилась сама, значит перетекла в соседние ткани. Число объёма недостоверно. Сузьте диапазон (для контрастной селезёнки ±40…±60) и повторите.</td></tr>`;
      } else if (!м.ошибка && м.пиксели?.length === visited.length) {
        const желаемый = options.segIndex || 1;
        let индекс = желаемый;
        let пересел = '';
        if (занят(желаемый)) {
          индекс = 0;
          for (let к = 1; к <= 5; к++) { if (!занят(к)) { индекс = к; break; } }
          if (!индекс) {
            вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">НЕ записано: сегмент ${желаемый} уже занят вашей работой, а свободных сегментов (1…5) нет. Освободите один — смешивать чужую разметку с ростом области я не буду.</td></tr>`;
            индекс = -1;
          } else {
            пересел = ` (сегмент ${желаемый} занят вашей разметкой — она не тронута)`;
          }
        }
        if (индекс > 0) {
          const изменённые = [];
          for (let i = 0; i < visited.length; i++) {
            if (visited[i] && м.пиксели[i] !== индекс) изменённые.push(i);
          }
          запомнитьПравкуМаски(изменённые, м.пиксели, 'рост области');
          for (const i of изменённые) м.пиксели[i] = индекс;
          const зап = записатьМаску(м.пиксели);
          вМаску = зап.ок
            ? `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">записано ${изменённые.length.toLocaleString('ru-RU')} вокселей в сегмент ${индекс}${пересел} — доступно «Только орган в 3D», STL и NIfTI. Не то выросло — «↶ Вернуть маску» в панели «Сегментация».</td></tr>`
            : `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${зап.причина}</td></tr>`;
        }
      } else if (м.ошибка) {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${м.ошибка}</td></tr>`;
      } else {
        вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: размер маски не совпал с объёмом</td></tr>`;
      }
    } catch (e) {
      вМаску = `<tr><td style="${TD_STYLE}">В маску</td><td style="${TD_STYLE}">не записано: ${e.message}</td></tr>`;
    }

    showResultModal('📦 Region Grow 3D', `
      <table style="${TABLE_STYLE}">
        <tr><td style="${TD_STYLE}">Seed HU</td><td style="${TD_STYLE}">${seedHU}</td></tr>
        <tr><td style="${TD_STYLE}">Диапазон</td><td style="${TD_STYLE}">${low} — ${high} HU</td></tr>
        <tr><td style="${TD_STYLE}">Вокселей найдено</td><td style="${TD_STYLE}">${count.toLocaleString()}${count >= MAX_VOXELS ? ' (лимит!)' : ''}</td></tr>
        <tr><td style="${TD_STYLE}">Объём</td><td style="${TD_STYLE}"><strong>${volMl} мл</strong> (${volMm3.toFixed(0)} мм³)</td></tr>
        <tr><td style="${TD_STYLE}">Seed координаты</td><td style="${TD_STYLE}">[${seedX}, ${seedY}, ${seedZ}] — из вашей метки кистью</td></tr>
        ${полурешёткаРоста ? `<tr><td style="${TD_STYLE}">Сетка роста</td><td style="${TD_STYLE}">маска ½ шага (${dims.join('×')}); плотность — среднее мелкой восьмёрки. Граница грубее на один шаг маски; объёмы структур ⌀ до ~10 мм расходятся с полной маской до 6% (промерено фантомом) — мелкие очаги мерьте на полной маске</td></tr>` : ''}
        ${вМаску}
      </table>
      <div style="margin-top:8px;color:#8fa3c0;font-size:12px;">${рамка
        ? 'Рост разрешён ТОЛЬКО в 10 мм от ваших обводок — форма зоны повторяет орган, поэтому печень и мышцы за его пределами недостижимы. Внутри зоны рост всё равно идёт по плотности: прилежащая вплотную ткань той же плотности может попасть. Лечится обводками и кнопкой «↶ Вернуть маску».'
        : 'ВНИМАНИЕ: разметки нет — зоны нет, рост идёт по всему объёму и почти наверняка перетечёт в соседние ткани. Обведите орган кистью хотя бы на нескольких срезах.'}</div>
    `);
  
    showToast(`📦 Наращивание области: ${count.toLocaleString()} вокселей, ${volMl} мл`, 'success');
  } catch (e) { log.warn(`[clinicalTools] runRegionGrow3D failed:`, e.message); showToast('❌ Наращивание области: ' + e.message, 'error', 3000); }
}


// ══════════════════════════════════════════════════════════════
//  8. [12.0.52] УДАЛЕНО: «Переломы» (runFractureDetection)
// ══════════════════════════════════════════════════════════════
//
// «Подозрительный» воксель — кость > 300 HU с перепадом плотности > 500 HU
// к соседям. Но такой перепад есть на КАЖДОЙ границе кортикального слоя с
// мягкими тканями: кость 800 HU рядом с мышцей 40 HU — это 760 HU. Поэтому
// любая нормальная КТ давала «Обнаружены подозрительные зоны — проверьте
// визуально». Замер на синтетике без переломов: 17% «подозрительных»
// вокселей кости, 62 «подозрительных среза» из 64. Поиск переломов по
// плотности без модели кости невозможен; кнопка убрана.


// ══════════════════════════════════════════════════════════════
//  9. MedSAM — Интерактивная сегментация
// ══════════════════════════════════════════════════════════════

let _samPoints =[];
let _samActive = false;

export function activateMedSAM() {
  _samPoints =[];
  _samОкноЩелчка = null;
  _samУбратьМетки();
  _samActive = true;
  clearSamCache();
  _samНачатьСборТочек();
  // [11.1.55] Про Enter в подсказке было написано зря: считает кнопка
  // «Запуск», обработчика Enter в проекте нет. Владелец нажимал Enter и
  // получал молчание — подсказка сама вводила в заблуждение.
  showToast('MedSAM включён: щёлкните ВНУТРИ органа (Shift+щелчок — «это не он»), затем «Запуск»', 'info', 6000);
}

/**
 * [11.1.55] ЩЕЛЧКИ ТЕПЕРЬ СОБИРАЮТСЯ. Жалоба владельца: «точка не ставится».
 *
 * Причина простая и стыдная: addSAMPoint не вызывалась НИОТКУДА. Функция
 * написана, кнопки «Отмена» и «Очистить» её список правят, «Запуск» его
 * читает — а слушателя щелчка по снимку не было ни одного. Щёлкать можно было
 * вечно: _samPoints оставался пуст, и «Запуск» честно отвечал «добавьте хотя
 * бы одну точку». Ровно та же болезнь, что с маской, которая уходила в
 * таблицу: труба построена, последний стык не сделан.
 *
 * КООРДИНАТЫ. Кодировщик ждёт точку в пикселях СРЕЗА (preprocessForSAM
 * умножает её на 1024/ширина). Щелчок приходит в пикселях холста. Перевод —
 * canvasToWorld, дальше worldToIndex: тот же путь, которым берётся номер
 * среза, и тот же, что уже применён в lvEfTools для выбора зерна. Своего
 * способа не выдумываем.
 *
 * ОКНО. Слушаем ВСЕ плоские окна, а не одно осевое: врач щёлкает там, где
 * смотрит. Окно щелчка запоминается — «Запуск» возьмёт срез из него же,
 * иначе точка была бы из одного окна, а срез из другого.
 */
let _samОкноЩелчка = null;
let _samСнятьСлушатели = null;

// [12.0.52] Проверки окна и среза — samMaskMath (чистый модуль, без импортов).
const _samМат = _samМатМодуль;

function _samНачатьСборТочек() {
  _samПрекратитьСборТочек();
  const re = getRenderingEngine();
  if (!re) { showToast('Окна ещё не готовы', 'warning', 3000); return; }

  const снятия = [];
  let нашлось = 0;
  for (const vp of re.getViewports()) {
    // Объёмное окно (3D) щелчком не промерить: у него нет плоскости среза.
    if (typeof vp?.canvasToWorld !== 'function' || !vp.element) continue;
    // [11.1.58] ТИП ОКНА — ПО ПОЛЮ type, А НЕ ПО НАЛИЧИЮ МЕТОДА.
    //
    // В 11.1.56 здесь стояло `typeof vp.getImageIds === 'function'` как признак
    // стекового окна. Признак НЕВЕРНЫЙ: getImageIds есть и у объёмных окон
    // (BaseVolumeViewport.js:317). Фильтр выкинул ВСЕ окна до единого, и
    // владелец получил «Нет плоского окна со снимком» там, где двумя версиями
    // раньше исправно работало. Я взял этот признак на глаз, не проверив.
    //
    // Библиотека различает окна полем type (enums/ViewportType: 'stack',
    // 'orthographic', 'volume3d'), и в этом проекте так уже делают четыре
    // места — toolBindings:233, enterprisePanel:201, featureBindings:2481,
    // toolManager:802. Пятого способа не заводим.
    //
    // Нужны только срезовые окна тома: в нативном (стековом) может лежать
    // ДРУГАЯ серия — у него свой выбор серии в углу, — а маска живёт в томе
    // MPR. Закодировать один снимок и записать в маску другого значит молча
    // получить ерунду.
    if (vp.type !== 'orthographic') continue;
    const элемент = vp.element;
    const наКлик = (e) => {
      if (!_samActive) return;
      // Пока идёт набор точек, активный инструмент (кисть и прочие) не должен
      // получать этот щелчок — иначе врач заодно рисует.
      e.stopPropagation();
      e.preventDefault();
      const т = _samТочкаИзЩелчка(vp, e);
      if (!т.ок) {
        log.warn(`[MedSAM] щелчок в окне «${vp.id}» точку не дал: ${т.причина}`);
        showToast('Точка не поставлена: ' + т.причина, 'warning', 4000);
        return;
      }
      _samОкноЩелчка = vp.id;
      addSAMPoint(т.x, т.y, e.shiftKey ? 0 : 1, т.k);
      // [11.1.59] Метка на снимке. Владелец: «саму точку на печени я не вижу,
      // хотя всплывающее окно об установке было». Верно: я собирал щелчки и
      // ничего не рисовал — врач ставил точки наугад и не мог понять, попал ли
      // он туда, куда хотел. Рисуем на слое поверх окна, в координатах холста
      // (там же, где был щелчок), — обратный перевод не нужен и ошибиться в нём
      // негде.
      _samНарисоватьМетку(элемент, e.clientX - элемент.getBoundingClientRect().left,
        e.clientY - элемент.getBoundingClientRect().top, e.shiftKey ? 0 : 1, _samPoints.length);
    };
    элемент.addEventListener('click', наКлик, { capture: true });
    элемент.style.cursor = 'crosshair';
    снятия.push(() => {
      элемент.removeEventListener('click', наКлик, { capture: true });
      элемент.style.cursor = '';
    });
    нашлось++;
  }
  if (!нашлось) {
    const были = re.getViewports().map((v) => `${v.id}:${v.type}`).join(', ');
    log.warn(`[MedSAM] срезовых окон (orthographic) не нашлось. Есть: ${были || 'ни одного'}`);
    showToast('Нет срезовых окон MPR — включите раскладку со срезами', 'warning', 5000);
    return;
  }
  _samСнятьСлушатели = () => { for (const с of снятия) { try { с(); } catch (e) { /* */ } } };
  log.info(`[MedSAM] сбор точек включён, окон под щелчок: ${нашлось}`);
}

function _samПрекратитьСборТочек() {
  if (_samСнятьСлушатели) { _samСнятьСлушатели(); _samСнятьСлушатели = null; }
}

/** Щелчок по холсту → координаты в пикселях среза (их и ждёт кодировщик). */
function _samТочкаИзЩелчка(vp, e) {
  try {
    const r = vp.element.getBoundingClientRect();
    const мир = vp.canvasToWorld([e.clientX - r.left, e.clientY - r.top]);
    if (!мир) return { ок: false, причина: 'щелчок не перевёлся в координаты' };
    const данные = vp.getImageData?.();
    const dims = данные?.dimensions;
    if (!dims) return { ок: false, причина: 'у окна нет данных изображения' };
    // [12.0.52] Только окно, где виден «осевой» срез ТОМА (плоскость i–j):
    // кодировщик получает именно такой срез. Щелчок в боковом/лобовом окне
    // давал точку (i, j) и срез из фокуса этого окна — чужой срез.
    const мат = _samМат;
    const D = данные.direction || данные.imageData?.getDirection?.();
    if (мат && !мат.окноПоОсиK(vp.getCamera?.()?.viewPlaneNormal, D)) {
      return { ок: false, причина: 'точки MedSAM ставятся в окне, где видны поперечные срезы серии (обычно — осевое); в боковом и лобовом окне сеть не обводит' };
    }
    const idx = _samМирВИндекс(мир, данные);
    if (!idx) return { ок: false, причина: 'координаты не перевелись в номер пикселя' };
    const x = Math.round(idx[0]), y = Math.round(idx[1]), k = Math.round(idx[2]);
    if (!(x >= 0 && x < dims[0] && y >= 0 && y < dims[1])) {
      return { ок: false, причина: `щелчок вне снимка (${x},${y})` };
    }
    return { ок: true, x, y, k };
  } catch (err) { return { ок: false, причина: err.message }; }
}

function _samМирВИндекс(мир, данные) {
  try {
    const id = данные.imageData;
    if (typeof id?.worldToIndex === 'function') {
      const из = [0, 0, 0];
      id.worldToIndex(мир, из);
      return из;
    }
    // Запасной путь — та же математика, что в rtContourOps.worldToIndex.
    const o = данные.origin || id?.getOrigin?.() || [0, 0, 0];
    const s = данные.spacing || id?.getSpacing?.() || [1, 1, 1];
    const D = данные.direction || id?.getDirection?.() || [1, 0, 0, 0, 1, 0, 0, 0, 1];
    const dx = мир[0] - o[0], dy = мир[1] - o[1], dz = мир[2] - o[2];
    return [
      (D[0] * dx + D[3] * dy + D[6] * dz) / s[0],
      (D[1] * dx + D[4] * dy + D[7] * dz) / s[1],
      (D[2] * dx + D[5] * dy + D[8] * dz) / s[2],
    ];
  } catch (err) { return null; }
}

/**
 * [11.1.77] Отдать готовую маску ТОМУ ЖЕ обработчику, что и «Импорт маски».
 *
 * ПОЧЕМУ ТАК, А НЕ СВОЕЙ ФУНКЦИЕЙ. Разбор .nii, распаковка gzip, чтение
 * ориентации и разворот под том, сверка с открытым исследованием, запоминание
 * правки для «Вернуть маску» — всё это двести строк в featureBindings, и они
 * наконец работают: владелец сегодня получил три органа, легших верно во всех
 * проекциях.
 *
 * Написать рядом вторую такую же — значит завести две дороги к одному делу.
 * Они разойдутся: в одной я поправлю ориентацию, в другой забуду, и врач будет
 * получать разный результат в зависимости от того, какой кнопкой вносил. Ровно
 * эту болезнь я весь день и разгребал.
 *
 * Выносить двести работающих строк в общую функцию — тоже риск, и сегодня не
 * ко времени. Поэтому файл подаётся В ТО ЖЕ ПОЛЕ ВЫБОРА, и срабатывает тот же
 * обработчик. Одна дорога, ноль переделок.
 */
async function _тсПодатьВИмпорт(буфер, имя) {
  const поле = document.getElementById('seg-import-file');
  if (!поле) throw new Error('поле выбора файла маски не найдено');
  const файл = new File([буфер], имя, { type: 'application/octet-stream' });
  const dt = new DataTransfer();
  dt.items.add(файл);
  поле.files = dt.files;
  поле.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * [11.1.77] Переключить активный сегмент, чтобы органы не слились в один.
 *
 * Щёлкаем по строке в списке — тем же способом, каким это делает врач. Своего
 * пути к активному сегменту не заводим: разойдись он с тем, что показано на
 * экране, и врач писал бы в один сегмент, а видел бы другой.
 */
/**
 * [12.0.0-правка] ЕСЛИ СЕГМЕНТА НЕТ — ЗАВОДИМ ЕГО, А НЕ ПИШЕМ В ТЕКУЩИЙ.
 *
 * ПОВОД — слова владельца: «такое ощущение, что масса от органа к органу идёт по
 * нарастающей… в этом перечне нет объёма селезёнки, как будто места не осталось».
 *
 * Места и правда не осталось, и «нарастания массы» никакого нет. Вот что было по
 * его снимкам:
 *     аорта         → сегмент 1
 *     жёлчный       → сегмент 2
 *     почка левая   → сегмент 3
 *     почка правая  → сегмент 4
 *     печень        → сегмент 5   1576 мл
 *     поджелудочная → сегмент 5   1694 мл
 *     селезёнка     → сегмент 5   2318 мл
 *
 * В панели ПЯТЬ строк сегментов, шестой и седьмой нет. Прежняя эта функция,
 * не найдя строку, писала строкой в отладку «пишу в текущий» и возвращала false —
 * а вызывающий этого не проверял. Три органа легли в один сегмент, и показанный
 * объём — не объём органа, а СУММА содержимого сегмента:
 *     1576 (печень) + 118 (поджелудочная) + 626 (селезёнка) = 2320 мл.
 * Отсюда и «нарастание». Объёма селезёнки в перечне нет потому, что своего
 * сегмента у неё не было.
 *
 * ХУЖЕ ТОГО: маски трёх органов в одном сегменте уже не разделить. Это не
 * «неудобно» — это негодная разметка, поданная как готовая.
 *
 * Теперь нехватающий сегмент заводится нажатием той же кнопки «+ Добавить
 * сегмент», которой пользуется врач: своего способа создавать строки не заводим,
 * иначе появится вторая дорога к одному делу и они разойдутся.
 */
function _тсВыбратьСегмент(номер) {
  try {
    const найти = () => [...document.querySelectorAll('#segment-list .seg-row')]
      .find((с) => Number(с.dataset?.segIndex) === номер);

    let нужная = найти();
    if (!нужная) {
      // Кнопка выдаёт номер «максимальный существующий + 1», поэтому жмём её,
      // пока не появится нужный. Предел назван числом: без него опечатка в
      // номере дала бы бесконечное нажатие.
      const кнопка = document.getElementById('btn-add-seg');
      if (!кнопка) {
        log.warn(`[TotalSeg] сегмента ${номер} нет и кнопки «Добавить сегмент» нет — `
          + 'маска легла бы в чужой сегмент, поэтому не пишу');
        return false;
      }
      for (let к = 0; к < 64 && !найти(); к++) кнопка.click();
      нужная = найти();
      if (!нужная) {
        log.warn(`[TotalSeg] сегмент ${номер} создать не удалось — не пишу, чтобы не смешать органы`);
        return false;
      }
      log.info(`[TotalSeg] сегмент ${номер} заведён`);
    }
    нужная.click();
    return true;
  } catch (e) {
    log.warn('[TotalSeg] выбор сегмента сорвался:', e.message);
    return false;
  }
}

/**
 * [11.1.59] МЕТКИ ТОЧЕК НА СНИМКЕ.
 *
 * Слой — обычный div поверх окна, с pointer-events: none, чтобы он не съедал
 * щелчки. Кружок зелёный для «внутри органа», красный для «это не он», с
 * номером рядом: врач видит, что и в каком порядке поставил.
 *
 * Почему не аннотации Cornerstone: они живут в мировых координатах и попадают
 * в общий список измерений и в автосохранение. Метки SAM — черновик на один
 * расчёт, им там не место.
 */
function _samНарисоватьМетку(элемент, x, y, метка, номер) {
  try {
    let слой = элемент.querySelector('.sam-точки');
    if (!слой) {
      слой = document.createElement('div');
      слой.className = 'sam-точки';
      слой.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:40;';
      элемент.style.position = элемент.style.position || 'relative';
      элемент.appendChild(слой);
    }
    const цвет = метка === 1 ? '#22c55e' : '#ef4444';
    const т = document.createElement('div');
    т.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:12px;height:12px;`
      + `margin:-6px 0 0 -6px;border-radius:50%;background:${цвет};`
      + 'box-shadow:0 0 0 2px #000, 0 0 6px rgba(0,0,0,.9);';
    const п = document.createElement('div');
    п.textContent = String(номер);
    п.style.cssText = `position:absolute;left:${x + 9}px;top:${y - 9}px;color:${цвет};`
      + 'font:700 11px/1 system-ui;text-shadow:0 0 3px #000,0 0 3px #000;';
    слой.appendChild(т);
    слой.appendChild(п);
  } catch (e) { log.debug('[MedSAM] метка не нарисовалась:', e.message); }
}

/** Убрать все метки со всех окон. */
function _samУбратьМетки() {
  try { document.querySelectorAll('.sam-точки').forEach((с) => с.remove()); }
  catch (e) { log.debug('[MedSAM] метки не убрались:', e.message); }
}

/** Снять метку последней точки (кнопка «Отмена»). Кружок и номер — две штуки. */
function _samУбратьПоследнююМетку() {
  try {
    for (const слой of document.querySelectorAll('.sam-точки')) {
      if (слой.childElementCount >= 2) {
        слой.lastElementChild.remove();
        слой.lastElementChild.remove();
        return;
      }
    }
  } catch (e) { log.debug('[MedSAM] метка не снялась:', e.message); }
}

/**
 * [11.1.62] Окно яркости окна просмотра как {center, width} в единицах HU.
 *
 * Cornerstone хранит его как voiRange {lower, upper}. Если окно его не отдаёт
 * (бывает до первой отрисовки), возвращаем мягкотканное W400/L40 и ПОМЕЧАЕМ
 * это — чтобы в журнале было видно, по какой картинке считала сеть.
 */
function _samОкноЯркости(vp) {
  try {
    const v = vp?.getProperties?.()?.voiRange;
    if (v && Number.isFinite(v.lower) && Number.isFinite(v.upper) && v.upper > v.lower) {
      return {
        center: Math.round((v.lower + v.upper) / 2),
        width: Math.round(v.upper - v.lower),
        изОкна: true,
      };
    }
  } catch (e) { log.debug('[MedSAM] окно яркости не прочиталось:', e.message); }
  return { center: 40, width: 400, изОкна: false };
}

/** Окно, в котором врач ставил точки; «Запуск» берёт срез из него же. */
export function samОкноЩелчка() { return _samОкноЩелчка; }

/**
 * [11.1.56] ОДИН ХОЗЯИН У ПОНЯТИЯ «ТЕКУЩИЙ СРЕЗ»: пиксели и его номер
 * берутся здесь вместе, одним расчётом.
 *
 * ДВЕ ОШИБКИ, КОТОРЫЕ ЭТО ЗАКРЫВАЕТ.
 *
 * 1. «Нет данных текущего среза» (жалоба владельца). getSlicePixelData читала
 *    imgData.scalarData. У объёмного окна это ГЕТТЕР, и внутри он зовёт
 *    voxelManager.getScalarData(), который у потокового тома БРОСАЕТ
 *    'No scalar data available' (VoxelManager.js:286 — падает, когда массив не
 *    собран целиком). Бросок ловился и писался на уровне debug, то есть
 *    невидимо. Дальше проверялось imgData.getPointData — а у этого объекта
 *    такого метода нет вовсе (getPointData живёт на imgData.imageData). Оба
 *    пути молча отваливались.
 *    Правильный способ давно применён в organView.томМаски:
 *    voxelManager.getCompleteScalarDataArray(). Он и берётся.
 *
 * 2. РАЗЪЕЗД НОМЕРА СРЕЗА. Пиксели брались по getCurrentImageIdIndex, а маска
 *    писалась по номеру из координат камеры. При нормали окна [0,0,-1] это
 *    РАЗНЫЕ срезы: сеть обводила бы один снимок, а закрашивался бы другой, с
 *    противоположного края исследования. Теперь номер считается один раз и
 *    отдаётся обоим.
 *
 * ПОЧЕМУ ТОЛЬКО ОБЪЁМНЫЕ ОКНА. Нативное окно — стековое, и в нём может лежать
 * ДРУГАЯ серия (в углу есть свой выбор серии). Маска же живёт в томе MPR.
 * Закодировать пиксели одной серии и записать в маску другой — молча получить
 * ерунду. Поэтому стековые окна из сбора исключены.
 *
 * @returns {{ок: true, пиксели: ArrayLike<number>, w: number, h: number, k: number, dims: number[], spacing: number[]}
 *          |{ок: false, причина: string}}
 */
function _samСрезИзОкна(vp) {
  try {
    if (!vp) return { ок: false, причина: 'окно не найдено' };
    // [11.1.58] Тот же признак, что при сборе точек: поле type, а не наличие
    // метода getImageIds (он есть и у объёмных окон).
    if (vp.type !== 'orthographic') {
      return { ок: false, причина: `окно «${vp.id}» типа «${vp.type}» — нужны срезовые проекции MPR (осевая, боковая, лобовая)` };
    }
    const и = vp.getImageData?.();
    if (!и?.dimensions) return { ок: false, причина: 'у окна нет данных изображения' };
    const [w, h, nz] = и.dimensions;
    if (!(w > 0 && h > 0 && nz > 0)) return { ок: false, причина: `у окна нет размеров (${и.dimensions.join('×')})` };

    // Пиксели: сперва тот способ, который работает у потоковых томов.
    let все = null;
    try { все = и.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) { все = null; }
    if (!все?.length) { try { все = и.scalarData; } catch (e) { все = null; } }
    if (!все?.length) {
      return { ок: false, причина: 'пиксели тома недоступны — исследование ещё грузится?' };
    }

    // Номер среза — из координат камеры, а не у окна (см. [11.1.55]).
    const камера = vp.getCamera?.();
    if (!камера?.focalPoint) return { ок: false, причина: 'у окна нет камеры' };
    const idx = _samМирВИндекс(камера.focalPoint, и);
    if (!idx) return { ок: false, причина: 'точка взгляда не перевелась в номер среза' };
    let k = Math.round(idx[2]) || 0;
    if (k < 0 && idx[2] > -1.75) k = 0;
    if (k >= nz && idx[2] < nz + 0.75) k = nz - 1;
    if (!(k >= 0 && k < nz)) return { ок: false, причина: `срез ${k} вне тома (0…${nz - 1})` };

    const наСрез = w * h;
    if (все.length < наСрез * nz) {
      return { ок: false, причина: `в томе ${все.length} пикселей, а нужно ${наСрез * nz} — данные не собраны` };
    }
    const пиксели = все.subarray
      ? все.subarray(k * наСрез, (k + 1) * наСрез)
      : все.slice(k * наСрез, (k + 1) * наСрез);
    return { ок: true, пиксели, w, h, k, dims: и.dimensions, spacing: и.spacing };
  } catch (e) { return { ок: false, причина: e.message }; }
}

export function addSAMPoint(x, y, label, k) {
  _samPoints.push({ x, y, label, k });
  // [11.1.57] В ЖУРНАЛ, а не только всплывающей подсказкой. По журналу
  // владельца нельзя было сказать, встали точки или нет: addSAMPoint писала
  // только тост, а тосты в файл не идут. Ровно та же слепота, что была на
  // импорте маски.
  log.info(`[MedSAM] точка ${_samPoints.length}: [${Math.round(x)},${Math.round(y)}] `
    + `${label === 1 ? 'внутри органа' : 'НЕ орган'}, окно «${_samОкноЩелчка}»`);
  showToast(`Точка ${_samPoints.length}:[${Math.round(x)},${Math.round(y)}] ${label === 1 ? '+' : '−'}`, 'info', 1200);
}

export function undoSAMPoint() {
  if (_samPoints.length) {
    _samPoints.pop();
    _samУбратьПоследнююМетку();
    log.info(`[MedSAM] точка снята, осталось ${_samPoints.length}`);
    showToast(`↩ Точек: ${_samPoints.length}`, 'info', 600);
  } else {
    // [12.0.56] Без точек кнопка молчала — на стенде вкладки ИИ это была
    // единственная кнопка без ответа. Молчащая кнопка неотличима от мёртвой.
    showToast('Точек MedSAM нет — отменять нечего', 'info', 2000);
  }
}

export function clearSAMPoints() {
  _samPoints =[];
  _samОкноЩелчка = null;
  _samУбратьМетки();
  clearSamCache();
  showToast('Точки убраны — щёлкайте заново', 'info', 1500);
}

export async function runMedSAMSegmentation() {
  try {
    if (_samPoints.length === 0) {
      showToast('⚠️ Добавьте хотя бы одну точку (кликните на изображение)', 'warning');
      return;
    }
  
    const encoderOk = await isModelAvailable('medsam-encoder');
    const decoderOk = await isModelAvailable('medsam-decoder');
  
    if (!encoderOk || !decoderOk) {
      showToast('❌ ONNX-модели MedSAM не найдены в /моделей/. Проверьте vision_encoder_quantized.onnx и sam_vit_b_01ec64.decoder.onnx', 'error');
      return;
    }
  
    showToast('🤖 MedSAM: кодирование изображения...', 'info');
  
    // [FIX] Явно указываем axial — без viewportId мог вернуть данные не того среза
    // [11.1.56] Срез — ОДНИМ расчётом: и пиксели, и его номер. Прежде пиксели
    // брала getSlicePixelData (и падала на потоковом томе), а номер считался
    // отдельно из камеры — при нормали окна [0,0,-1] это разные срезы.
    const re0 = getRenderingEngine();
    const vpТочек = re0?.getViewport?.(_samОкноЩелчка) || re0?.getViewport?.('axial');
    const срез = _samСрезИзОкна(vpТочек);
    if (!срез.ок) {
      log.warn(`[MedSAM] срез не прочитан (окно «${_samОкноЩелчка}»): ${срез.причина}`);
      showToast('Срез не прочитан: ' + срез.причина, 'warning', 7000);
      return;
    }
    // [12.0.52] Точки и срез — один и тот же срез.
    const наСрезе = _samМат?.точкиНаСрезе(_samPoints, срез.k);
    if (наСрезе && !наСрезе.ok) { showToast('MedSAM: ' + наСрезе.причина, 'warning', 8000); return; }
    log.info(`[MedSAM] запуск: окно «${_samОкноЩелчка}», срез ${срез.k}, `
      + `сетка ${срез.w}×${срез.h}, точек ${_samPoints.length}`);
    const slice = { pixelData: срез.пиксели, width: срез.w, height: срез.h, sliceIdx: срез.k };
  
    try {
      // [11.1.59] ОТЧЁТ НА КАЖДОМ ШАГЕ. В журнале владельца между «запуск» и
      // результатом не было НИ ОДНОЙ строки: кодирование заняло 26 секунд,
      // декодер начал грузиться — и на этом журнал кончался. Понять, добежал
      // ли декодер, было нельзя. Теперь каждый шаг отчитывается со временем.
      const t0 = Date.now();
      // [11.1.62] СЕТЬ ДОЛЖНА ВИДЕТЬ ТО ЖЕ, ЧТО ВРАЧ.
      //
      // Здесь окно яркости не передавалось вовсе, и preprocessForSAM брал своё
      // значение по умолчанию — жёстко зашитое W400/L40 (aiModule.js:646).
      // Врач же смотрит на свою настройку. На артериальной фазе печень при
      // W400/L40 почти не отличается от окружающих мягких тканей — и сеть,
      // получив такую картинку, обводит всё туловище. Именно это владелец и
      // увидел: 54,7% площади среза при верно поставленной точке.
      //
      // Берём окно из САМОГО ОКНА ПРОСМОТРА. Оно же входит в ключ кэша
      // (aiModule.js:681), поэтому смена яркости честно вызывает пересчёт, а не
      // отдаёт старые признаки.
      const окно = _samОкноЯркости(vpТочек);
      log.info(`[MedSAM] окно яркости для сети: центр ${окно.center}, ширина ${окно.width}`
        + `${окно.изОкна ? ' (из окна просмотра)' : ' (окно просмотра не отдало — взято мягкотканное)'}`);
      const embedding = await samEncodeImage(
        slice.pixelData, slice.width, slice.height,
        `slice-${slice.sliceIdx}`, окно
      );
      if (!embedding?.embedding) {
        log.warn('[MedSAM] кодировщик не отдал признаки среза — модель не загрузилась или срез пуст');
        showToast('Кодировщик не отдал признаки среза. Проверьте vision_encoder_quantized.onnx в папке models', 'error', 8000);
        return;
      }
      log.info(`[MedSAM] срез закодирован за ${((Date.now() - t0) / 1000).toFixed(1)} с, `
        + `масштаб ${embedding.scaleX?.toFixed?.(3)}×${embedding.scaleY?.toFixed?.(3)}`);
      showToast('MedSAM: декодирование маски…', 'info');

      const t1 = Date.now();
      const result = await samDecodePoints(_samPoints, embedding);
      // samDecodePoints при сбое возвращает ПУСТОЙ объект (его собственный
      // catch). Дальше стояло result.iou.toFixed(3) без защиты — и вместо
      // внятного отказа врач получал «Cannot read properties of undefined».
      if (!result?.mask?.length) {
        log.warn(`[MedSAM] декодер не отдал маску за ${((Date.now() - t1) / 1000).toFixed(1)} с `
          + '— проверьте sam_vit_b_01ec64.decoder.onnx в папке models');
        showToast('Декодер не отдал маску. Проверьте sam_vit_b_01ec64.decoder.onnx в папке models', 'error', 8000);
        return;
      }
      // Считаем без Array.from: копия массива на миллион элементов здесь ни к
      // чему, а главное — счёт не должен зависеть от того, стоит ли выше
      // проверка на пустоту. Порядок строк — плохая опора.
      let вМаске = 0;
      for (let i = 0; i < (result.mask?.length || 0); i++) if (result.mask[i] > 0) вМаске++;
      log.info(`[MedSAM] маска получена за ${((Date.now() - t1) / 1000).toFixed(1)} с: `
        + `${result.maskWidth}×${result.maskHeight}, IoU ${result.iou?.toFixed?.(3) ?? '?'}, `
        + `вокселей в маске ${вМаске}`);
      if (!вМаске) {
        log.warn('[MedSAM] маска пустая: сеть не нашла объект вокруг поставленной точки');
        showToast('Сеть вернула пустую маску: точка попала не туда или орган плохо отделим. Поставьте точку в центр органа и добавьте вторую', 'warning', 9000);
        return;
      }
  
      // [11.1.54] МАСКА ТЕПЕРЬ ЛОЖИТСЯ НА ХОЛСТ, А НЕ В ТАБЛИЦУ.
      //
      // До этой версии здесь считалась маска с IoU — и показывалась таблицей.
      // записатьМаску не вызывалась ни разу: сеть отрабатывала, а результат
      // выбрасывался. Врач ради селезёнки делал двенадцать обводок руками,
      // притом что модель обводила её одним щелчком в память и в память же
      // это и оставалось.
      //
      // Труба была построена на девяносто процентов: encoder, decoder, реестр
      // моделей, откаты — всё на месте. Не хватало последних десяти
      // сантиметров: перевода маски 1024×1024 в сетку тома и записи по тому же
      // пути, которым ходит импорт .nii (organView.записатьМаску).
      const положено = await _положитьМаскуSAMнаХолст(result, срез);
      if (!положено.ок) {
        log.warn(`[MedSAM] маска посчитана (IoU ${result.iou?.toFixed?.(3)}), но на холст не легла: ${положено.причина}`);
        showToast('Маска посчитана, но на холст не легла: ' + положено.причина, 'warning', 7000);
      }
  
      // [11.1.61] Захват всего тела виден врачу СРАЗУ и с предложением отмены,
      // а не выдаётся за успех. Отменяем той же кнопкой, что и всё остальное:
      // organView.отменитьПравкуМаски — правку мы запомнили выше.
      if (положено.ок && положено.площадь?.подозрительно) {
        const { отменитьПравкуМаски } = await import('../core/organView.js');
        // [11.1.62] Подсказка по делу, а не «поставьте точнее». Одной
        // положительной точки SAM'у мало: он берёт крупнейшую связную область
        // похожей яркости, и на артериальной фазе это всё туловище. Лечится
        // ОТРИЦАТЕЛЬНЫМИ точками — Shift+щелчок по тому, что органом НЕ
        // является. Про них до сих пор нигде не было сказано внятно, и врач,
        // естественно, ими не пользовался.
        const убрать = confirm(
          `Маска заняла ${положено.площадь.процент}% площади среза (${положено.мл.toFixed(1)} мл).\n\n`
          + 'Похоже, сеть захватила всё тело, а не орган. Уверенность сети (IoU) на это не указывает — '
          + 'она бывает высокой и при неверной обводке.\n\n'
          + 'ЧТО ПОМОГАЕТ: не менять точку, а ДОБАВИТЬ отрицательные — Shift+щелчок по тому, что органом '
          + 'не является (лёгкое, селезёнка, мышцы спины, позвонок). Точки накапливаются, «Запуск» '
          + 'считает по всем сразу. Обычно хватает двух-трёх.\n\nОтменить эту маску?');
        if (убрать) {
          const о = отменитьПравкуМаски();
          log.info(`[MedSAM] врач отменил подозрительную маску: ${о.ок ? 'откатано' : о.причина}`);
          showToast(о.ок
            ? `Маска отменена. Точка на печени осталась — добавьте Shift+щелчок по лёгкому и по селезёнке, затем «Запуск». Сейчас точек: ${_samPoints.length}`
            : 'Отменить не удалось: ' + о.причина, о.ок ? 'info' : 'warning', 9000);
          return result;
        }
      }
      const iouТекст = result.iou?.toFixed?.(3) ?? '?';
      showToast(положено.ок
        ? `Маска на холсте: ${положено.мл.toFixed(1)} мл, IoU=${iouТекст}`
        : `MedSAM: маска ${result.maskWidth}×${result.maskHeight}, IoU=${iouТекст}`,
        положено.ок ? 'success' : 'warning');
  
      showResultModal('🎯 MedSAM Segmentation', `
        <table style="${TABLE_STYLE}">
          <tr><td style="${TD_STYLE}">Размер маски</td><td style="${TD_STYLE}">${result.maskWidth} × ${result.maskHeight}</td></tr>
          <tr><td style="${TD_STYLE}">IOU score</td><td style="${TD_STYLE}">${result.iou?.toFixed?.(4) ?? '—'}</td></tr>
          <tr><td style="${TD_STYLE}">Точек использовано</td><td style="${TD_STYLE}">${_samPoints.length}</td></tr>
          <tr><td style="${TD_STYLE}">Оригинал</td><td style="${TD_STYLE}">${result.origWidth} × ${result.origHeight}</td></tr>
          <tr><td style="${TD_STYLE}">Вокселей в маске</td><td style="${TD_STYLE}">${вМаске.toLocaleString()}</td></tr>
          <tr><td style="${TD_STYLE}">На холсте</td><td style="${TD_STYLE}">${положено.ок ? `срез ${положено.срез}, вокселей ${положено.вокселей.toLocaleString()}, ${положено.мл.toFixed(1)} мл` : 'нет — ' + положено.причина}</td></tr>
          ${положено.ок ? `<tr><td style="${TD_STYLE}">Доля площади среза</td><td style="${TD_STYLE}">${положено.площадь.процент}%${положено.площадь.подозрительно ? ' — ПОДОЗРИТЕЛЬНО МНОГО, похоже на всё тело' : ''}</td></tr>` : ''}
        </table>
      `);
  
      return result;
  
    } catch (err) {
      showToast(`❌ MedSAM ошибка: ${err.message}`, 'error');
      log.error('[MedSAM]', err);
    }
  } catch (e) { log.warn(`[clinicalTools] runMedSAMSegmentation failed:`, e.message); return null; }
}

/**
 * [11.1.54] МАСКА MedSAM → ХОЛСТ СЕГМЕНТАЦИИ.
 *
 * Идёт тем же путём, что импорт .nii: томМаски → правка копии массива →
 * записатьМаску → запомнитьПравкуМаски. Второй дороги к маске не заводим.
 *
 * ГЛАВНАЯ ТОНКОСТЬ — НОМЕР СРЕЗА. У окна его НЕ СПРАШИВАЕМ. В журнале
 * владельца нормаль осевого окна равна [0, 0, -1]: счёт срезов у окна идёт с
 * противоположного конца тома, и getCurrentImageIdIndex вернул бы номер,
 * зеркальный нужному. Маска печени легла бы на срез с другого края
 * исследования — и это выглядело бы как «сеть врёт», хотя сеть права.
 *
 * Поэтому берём точку взгляда камеры в МИРОВЫХ координатах и переводим её в
 * индексы вокселей тем же worldToIndex, которым ходит модуль RTSTRUCT (он
 * учитывает direction, то есть и косые серии). Знак и порядок осей при этом
 * получаются из геометрии самого тома, а не из наших предположений.
 *
 * @param {{mask: ArrayLike<number>, maskWidth: number, maskHeight: number}} результат
 * @returns {Promise<{ок: boolean, причина?: string, срез?: number, вокселей?: number, мл?: number}>}
 */
async function _положитьМаскуSAMнаХолст(результат, срезОкна) {
  try {
    if (!результат?.mask?.length) return { ок: false, причина: 'декодер не отдал маску' };

    const [{ томМаски, записатьМаску, запомнитьПравкуМаски }, мат] = await Promise.all([
      import('../core/organView.js'),
      import('../ai/samMaskMath.js'),
    ]);

    // [11.1.60] ХОЛСТ ЗАВОДИМ САМИ — ТУ ЖЕ ДВЕРЬ, ЧТО У ИМПОРТА .nii.
    //
    // Владелец получил в окне результата: «На холсте: нет — Сегментации ещё
    // нет: возьмите кисть и проведите по снимку». При IoU 0,93 и 13 279
    // вокселях в маске. То есть сеть отработала, а положить результат было
    // некуда.
    //
    // Ровно это я лечил в 11.1.52 для импорта маски: добавил публичный вход
    // toolManager.обеспечитьХолстМаски. Но к пути MedSAM ту же дверь не
    // подключил — сделал половину работы и не проверил вторую. Требование
    // «сперва помажьте кистью» здесь так же абсурдно, как было там: человек
    // получил ГОТОВУЮ разметку от сети.
    let м = томМаски();
    // getActiveSegmentationId берём из того же динамического импорта, что и
    // саму дверь: статический ввоз toolManager в этот файл тянул бы за собой
    // весь движок инструментов ради одной проверки.
    const тм = await import('../tools/toolManager.js');
    if (м.ошибка && !тм.getActiveSegmentationId()) {
      log.info('[MedSAM] холста нет — готовлю под маску');
      const { обеспечитьХолстМаски } = тм;
      const готов = await обеспечитьХолстМаски();
      if (!готов) {
        return { ок: false, причина: 'холст не создался: маске нужны срезовые окна MPR с загруженной серией' };
      }
      м = томМаски();
    }
    if (м.ошибка) return { ок: false, причина: м.ошибка };
    const [nx, ny, nz] = м.dims || [];

    // [11.1.56] Номер среза НЕ пересчитываем: он пришёл из того же расчёта,
    // которым брались пиксели для кодировщика (_samСрезИзОкна). Пересчёт здесь
    // и был вторым хозяином одного понятия — сеть могла обвести один снимок, а
    // закраситься другой.
    const срез = мат.номерСреза(срезОкна?.k, nz);
    if (!срез.ок) return { ок: false, причина: срез.причина };

    // Сетка окна и сетка маски обязаны совпадать: маска — производный том от
    // того же исследования. Разойдись они — писали бы не туда.
    if (срезОкна?.w !== nx || срезОкна?.h !== ny) {
      return { ок: false, причина: `сетка окна ${срезОкна?.w}×${срезОкна?.h} не сходится с маской ${nx}×${ny}` };
    }

    const масшт = мат.масштабироватьМаску(результат.mask, результат.maskWidth, результат.maskHeight, nx, ny);
    if (!масшт.ок) return { ок: false, причина: масшт.причина };

    const инд = мат.индексыВТоме(масшт.маска, м.dims, срез.k);
    if (!инд.ок) return { ок: false, причина: инд.причина };
    if (!инд.индексы.length) return { ок: false, причина: 'после переноса в сетку тома не осталось ни одного вокселя' };

    // Номер сегмента — тот, что выбран в списке; иначе первый.
    const сегмент = _активныйНомерСегмента();

    const пиксели = м.пиксели;
    // Прежние значения снимаем ДО правки — иначе «Вернуть маску» вернёт уже
    // изменённое (та же причина описана в organView.запомнитьПравкуМаски).
    const прежние = new Uint8Array(инд.индексы.length);
    for (let i = 0; i < инд.индексы.length; i++) прежние[i] = пиксели[инд.индексы[i]];
    for (let i = 0; i < инд.индексы.length; i++) пиксели[инд.индексы[i]] = сегмент;

    const зап = записатьМаску(пиксели);
    if (!зап.ок) return { ок: false, причина: зап.причина };
    запомнитьПравкуМаски(инд.индексы, пиксели, `MedSAM, срез ${срез.k}`, прежние);

    const мл = мат.объёмМлПоВокселям(инд.индексы.length, м.spacing);
    // [11.1.61] Сторож площади. IoU признаком качества не служит: на срезе 404
    // сеть доложила 0,9326 и обвела всё туловище. Доля площади среза — служит.
    const пл = мат.похоже_на_всё_тело(инд.индексы.length, nx, ny);
    log.info(`[MedSAM] на холст: срез ${срез.k}${срез.притянут ? ' (притянут к краю)' : ''}, `
      + `сегмент ${сегмент}, вокселей ${инд.индексы.length}, ${мл.toFixed(1)} мл, `
      + `доля среза ${пл.процент}%, `
      + `маска ${результат.maskWidth}×${результат.maskHeight} → срез ${nx}×${ny}`);
    if (пл.подозрительно) {
      log.warn(`[MedSAM] маска заняла ${пл.процент}% среза — похоже, сеть захватила всё тело, а не орган`);
    }
    return { ок: true, срез: срез.k, вокселей: инд.индексы.length, мл, площадь: пл };
  } catch (e) {
    log.warn('[MedSAM] запись на холст:', e.message);
    return { ок: false, причина: e.message };
  }
}

/**
 * Номер активного сегмента. Селектор и атрибут — те же, что в
 * featureBindings._активныйСегмент (`#segment-list .seg-row.active`,
 * `data-seg-index`, введён в 11.1.21). Своего соглашения не заводим: разойдись
 * они — MedSAM писал бы в один сегмент, а объём считался бы по другому.
 */
function _активныйНомерСегмента() {
  try {
    const строка = document.querySelector('#segment-list .seg-row.active')
      || document.querySelector('#segment-list .seg-row');
    const н = Number(строка?.dataset?.segIndex);
    return Number.isFinite(н) && н > 0 ? н : 1;
  } catch (e) { return 1; }
}

export function isSAMActive() { return _samActive; }
export function deactivateMedSAM() {
  _samActive = false;
  _samPoints =[];
  _samОкноЩелчка = null;
  _samУбратьМетки();
  _samПрекратитьСборТочек();
}


// ══════════════════════════════════════════════════════════════
//  10. [12.0.52] УДАЛЕНО: «Диаметр аорты» (runAortaDiameter)
// ══════════════════════════════════════════════════════════════
//
// «Уровни» аорты брались долями длины серии (дуга — 25%, восходящая — 35%…),
// хотя у Cornerstone срез 0 — у ног: «дуга» приходилась на живот. «Аорта»
// на срезе — центр тяжести ВСЕХ пикселей 25–100 и 130–500 HU в средней
// части кадра (сердце, мышцы, печень, позвоночник), «диаметр» — пролёт от
// этого центра по тем же плотностям. Замер на синтетике с аортой 30 мм:
// 151…183 мм на всех четырёх «уровнях». Зелёный цвет давался до 40 мм и
// нисходящей аорте, у которой граница — 30 мм.
// Диаметр аорты сейчас меряют «Линейкой» на срезе, перпендикулярном
// сосуду; развёртка сосуда — «CPR сосуда» в этом же разделе.


// ══════════════════════════════════════════════════════════════
//  EXPORT: привязка кнопок (вызывается из uiManager.js)
// ══════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════
// 3D LABELMAP -> MPR Labelmap overlay (CS3D v2 API)
// ══════════════════════════════════════════════════════════════
// [12.0.56] applyLabelMapToCornerstone УДАЛЕНА: её звал только путь lungmask из
// карточки «Лёгкие», и клала она маску в ОТДЕЛЬНУЮ сегментацию «lungSeg3D» —
// мимо «Сегментации», без отмены, со своим флагом «Авто-наложение». Лёгкие
// теперь ложатся в сегменты тем же путём, что печень (ai/lungSeg.js).


/**
 * Bind click handlers to all clinical tool buttons.
 * This is the SINGLE source of button bindings — no stubs in uiManager.
 * @param {function():string} getVolumeId - Returns current volume ID
 */
export function bindAllClinicalTools(getVolumeId) {
  const $ = (id) => document.getElementById(id);
  const on = (id, evt, fn) => { const el = $(id); if (el) el.addEventListener(evt, fn, { signal: _ac.signal }); };

  // ─── Algorithmic tools (не требуют ONNX моделей) ──────────
  on('btn-agatston', 'click', () => runAgatstonScore(getVolumeId()));
  on('btn-emphysema', 'click', () => runEmphysemaQuantification(getVolumeId()));
  on('btn-ctr', 'click', () => runCTR(getVolumeId()));
  on('btn-bmd', 'click', () => runBMD(getVolumeId()));
  // [12.0.52] «Пневмоторакс», «Переломы», «Диаметр аорты» удалены — см. надгробия выше.

  on('btn-region-grow', 'click', () => {
    const range = parseInt($(('region-grow-range'))?.value || '100');
    runRegionGrow3D(getVolumeId(), { range });
  });

  // [12.0.7] Маска половинного шага — переключатель хранится в localStorage и
  // читается в момент СОЗДАНИЯ холста (toolManager.создатьХолст). На уже
  // созданную маску не влияет — об этом говорит подпись и тост.
  {
    const чек = $('seg-halfres'), знач = $('seg-halfres-val');
    const показать = (вкл) => { if (знач) знач.textContent = вкл ? 'вкл' : 'выкл'; };
    let вкл = false;
    try { вкл = localStorage.getItem('mikhmel.seg.halfres') === '1'; } catch (e) { /* приватный режим */ }
    if (чек) { чек.checked = вкл; показать(вкл); }
    on('seg-halfres', 'change', (e) => {
      const т = !!e.target.checked;
      try { localStorage.setItem('mikhmel.seg.halfres', т ? '1' : '0'); } catch (eх) { /* приватный режим */ }
      показать(т);
      showToast(т
        ? 'Маска ½ шага: подействует на СЛЕДУЮЩУЮ создаваемую маску (в 8 раз меньше памяти; пороговая кисть на ней выключена; мелкие очаги мерьте на полной)'
        : 'Маска ½ шага выключена: новые маски — полного шага', 'info', 7000);
    });
  }

  // ─── Cardiac ──────────────────────────────────────────────
  // [10.19.32] Сирота btn-cardiac-seg удалён: кнопки нет в разметке. Живой
  // вход один — карточка «Камеры сердца» вкладки ИИ (словарь data-model:
  // 'cardiac' → runCardiacSegmentation ниже).

  // ─── MedSAM (ONNX) ───────────────────────────────────────
  on('btn-medsam', 'click', () => activateMedSAM());
  on('btn-sam-undo', 'click', () => undoSAMPoint());
  on('btn-sam-clear', 'click', () => clearSAMPoints());
  on('btn-sam-run', 'click', () => runMedSAMSegmentation());

  // [11.1.79] 117 СТРУКТУР ОДНОЙ МОДЕЛЬЮ — БЕЗ УСТАНОВКИ ЧЕГО-ЛИБО.
  //
  // Кнопка «для разработчика» ниже требует Питона, пакета, библиотеки счёта на
  // видеокарте и 5 ГБ весов — врач этого не сделает. Здесь модель лежит файлом
  // в папке моделей и считается тем же движком, что печень.
  // [11.1.84] Список структур строится по выбранной модели — иначе, выбрав
  // «рёбра», врач видит девять брюшных органов и отметить нужное ему негде.
  // Подробности и повод — в organsPick.js.
  import('./organsPick.js')
    .then((м) => { if (!м.настроитьВыборСтруктур()) log.debug('[Органы] панели структур нет в разметке'); })
    .catch((e) => log.warn('[Органы] список структур не построился:', e.message));

  // ══════════════════════════════════════════════════════════════
  //  [11.1.88] СНИМОК НЕ ДОЛЖЕН ПРОПАДАТЬ ИЗ-ЗА РАЗМЕТКИ
  //
  //  Слова владельца: «после „разметить органы“ органы появились, но изображение
  //  исчезло, я нажал вернуть маску, органы исчезли, но и изображение не
  //  вернулось».
  //
  //  По журналу видно всё: разметка съела память до 85%, страж дошёл до крайней
  //  меры («сброшено непоказанных томов: 1») — и снимок исчез. «Вернуть маску»
  //  тут бессильно: оно откатывает МАСКУ, а пропал СНИМОК. Это две разные вещи,
  //  и я не дал врачу ничего, чтобы вернуть вторую.
  //
  //  Здесь три меры, и нужны все три:
  //    1. ДЕРЖАТЬ том, пока считает разметка: у стража есть на это ручка
  //       (keepVolumeShown), и она была заведена ровно для такого случая —
  //       наложением. Разметку я в неё вписать забыл.
  //    2. ПРОВЕРИТЬ после счёта, что пиксели на месте, и не ждать, пока врач
  //       заметит чёрный экран сам.
  //    3. ВЕРНУТЬ снимок, если он всё-таки пропал: серия открыта, список серий
  //       известен, перечитать её — работа программы, а не человека.
  // ══════════════════════════════════════════════════════════════

  // [12.0.32] Защита снимка вынесена в ui/snapshotGuard.js: теперь её же
  // используют органные кнопки 3D, которые шли в модель мимо неё. Здесь —
  // тонкие обёртки с прежними именами.
  const _держатьСнимок = async () => (await import('./snapshotGuard.js')).держатьСнимок();
  const _отпуститьСнимок = async (держим) => (await import('./snapshotGuard.js')).отпуститьСнимок(держим);
  const _проверитьСнимок = async (повод = 'счёт') => (await import('./snapshotGuard.js')).проверитьСнимок(повод);

  let _органыИдут = false;

  // [12.0.39] «Показать в 3D» — вся разметка поверхностями (organIsolate3D.
  // показатьРазметкуВ3D). Защита снимка — та же, что у счёта: сотня
  // поверхностей тоже ест память. Снимок пришлось вернуть — показ повторяется
  // сам, как у кнопки органов (12.0.37).
  let _в3дИдёт = false;
  on('btn-organs-3d', 'click', async () => {
    if (_в3дИдёт || _органыИдут) { showToast('Подождите — идёт разметка или показ', 'info', 2500); return; }
    const кнопка = document.getElementById('btn-organs-3d');
    const строка = document.getElementById('organs-hod');
    const показать = (тт) => { if (строка) строка.textContent = тт; };
    _в3дИдёт = true;
    if (кнопка) кнопка.disabled = true;
    try {
      const { окноДляОрганов } = await import('../core/organView.js');
      if (!окноДляОрганов()) {
        показать('включаю раскладку с 3D…');
        const { changeLayout } = await import('./layoutManager.js');
        await changeLayout('mpr6');
      }
      const { показатьРазметкуВ3D } = await import('../core/organIsolate3D.js');
      const ход = (доля, текст) => показать(`${Math.round(доля * 100)}% · ${текст}`);
      let держим = await _держатьСнимок();
      let р;
      try { р = await показатьРазметкуВ3D({ ход }); } finally { await _отпуститьСнимок(держим); }
      let пропадал = false;
      try { пропадал = (await import('../core/viewport.js')).окнаБезДанных().length > 0; } catch (e) { пропадал = false; }
      const цел = await _проверитьСнимок('показ разметки в 3D');
      if (цел && пропадал) {
        log.info('[Разметка-3D] снимок возвращали — показываю ещё раз');
        держим = await _держатьСнимок();
        try { р = await показатьРазметкуВ3D({ ход }); } finally { await _отпуститьСнимок(держим); }
      }
      показать('');
      if (!цел || !р) return;
      if (!р.ок) { showToast(`Показ в 3D: ${р.причина}`, 'warning', 7000); return; }
      const { ПО_РУССКИ } = await import('../ai/organsMath.js');
      const есть = р.органы.filter((о) => о.показан);
      const имена = есть.map((о) => (о.безИмени ? о.орган.replace('-', ' ') : (ПО_РУССКИ[о.орган] || о.орган)));
      const нет = р.органы.length - есть.length;
      const где = р.окно === 'view-3d' ? 'окно «3D · Пресет»' : 'объёмное окно';
      showToast(`В 3D: ${имена.slice(0, 12).join(', ')}${имена.length > 12 ? ` и ещё ${имена.length - 12}` : ''}`
        + `${нет ? `; не построилось: ${нет}` : ''} — ${где}`, нет ? 'warning' : 'success', 8000);
    } catch (e) {
      показать('');
      log.warn('[Разметка-3D]', e);
      showToast('Показ в 3D не удался: ' + e.message, 'error', 8000);
    } finally {
      _в3дИдёт = false;
      if (кнопка) кнопка.disabled = false;
    }
  });
  // «Убрать из 3D» — ровно то, что «Убрать органы» в панели 3D: одна логика.
  on('btn-organs-3d-off', 'click', () => {
    const к = document.getElementById('btn-organs-clear');
    if (к) к.click(); else showToast('Кнопка «Убрать органы» не найдена', 'warning', 3000);
  });

  on('btn-organs', 'click', async () => {
    if (_органыИдут) { showToast('Разметка уже идёт — дождитесь конца', 'info', 2500); return; }
    const кнопка = document.getElementById('btn-organs');
    const строка = document.getElementById('organs-hod');
    const показать = (тт) => { if (строка) строка.textContent = тт; };
    const органы = Array.from(document.querySelectorAll('.org-pick:checked')).map((э) => э.value);
    if (!органы.length) { showToast('Отметьте хотя бы одну структуру', 'info', 3000); return; }

    _органыИдут = true;
    if (кнопка) кнопка.disabled = true;
    // [11.1.88] Держим том на время счёта: разметка съедает память, и страж в
    // крайней мере сбрасывает тома. У владельца так пропал снимок.
    const держим = await _держатьСнимок();
    try {
      const { органыАвтоматом } = await import('../ai/organsSeg.js');
      // [11.1.80] Какой моделью считать. 6 мм — три заплаты, секунды;
      // 3 мм — пятьдесят четыре, минуты, но вдвое подробнее.
      // [11.1.87] Модель спрашиваем у organsPick: у полной модели в панели стоит
      // один переключатель «по областям», а сама область — под ним. Читать
      // значение переключателя напрямую значило бы получить заглушку.
      const { модельПанели } = await import('./organsPick.js');
      const выбрана = модельПанели();
      const р = await органыАвтоматом({
        модель: выбрана,
        органы,
        первыйСегмент: _активныйНомерСегмента(),
        ход: (доля, текст) => показать(`${Math.round(доля * 100)}% · ${текст}`),
      });
      if (!р.ок) {
        показать('');
        log.warn('[Органы] не вышло:', р.причина);
        showToast('Органы не разметились: ' + р.причина, 'warning', 12000);
        return;
      }
      const свод = р.итоги.map((и) => `${и.имя} ${и.мл.toFixed(0)} мл`).join('; ');
      показать(свод);
      // [11.1.89] Та же оговорка, что у сторонней разметки: если врач смотрит в
      // «Нативные срезы», маски он не увидит нигде — там стопка, холста нет.
      const гдеВидно = _тсГдеВиднаМаска();
      showToast(`Готово за ${(р.мс / 1000).toFixed(1)} с (заплат ${р.заплат}). ${свод}.`
        + (гдеВидно.предупредить
          ? ` Маска рисуется только в срезовых окнах${гдеВидно.окна.length ? ` (${гдеВидно.окна.join(', ')})` : ''};`
            + ' в «Нативных срезах» её нет — это стопка снимков. Переключитесь на MPR.'
          : '')
        + ' Не понравилось — «Вернуть маску»', 'success', 18000);
    } catch (e) {
      показать('');
      log.warn('[Органы] сорвалось:', e.message);
      showToast('Органы: ' + e.message, 'error', 9000);
    } finally {
      await _отпуститьСнимок(держим);
      // Проверяем ВСЕГДА, в том числе после срыва: память кончается как раз
      // тогда, когда счёт не дошёл до конца.
      await _проверитьСнимок('разметку органов');
      _органыИдут = false;
      if (кнопка) кнопка.disabled = false;
    }
  });

  // [11.1.88] Отдельная кнопка на случай, когда снимок всё-таки пропал: врачу
  // нужен способ вернуть его, не перезагружая исследование целиком.
  on('btn-seg-снимок', 'click', async () => {
    const { окнаБезДанных } = await import('../core/viewport.js');
    if (!окнаБезДанных().length) {
      showToast('Снимок на месте — возвращать нечего', 'info', 3500);
      return;
    }
    await _проверитьСнимок('нажатие кнопки');
  });

  // [11.1.80] СПРАВКА ПО ЗНАКУ ВОПРОСА: что установить и какими командами.
  //
  // Слова владельца: «желательно сделать кнопку знак вопроса, при щелчке на
  // которую будут показаны необходимые для установки программ команды с
  // возможностью их копирования». Прямо в панели держать их нельзя — там
  // английские команды, а в интерфейсе программы всё по-русски (сторож
  // russian_ui_test за этим следит, и он меня дважды поймал). В окне справки
  // они уместны: это выписка для командной строки, а не подпись кнопки.
  //
  // Команды выписаны из ЭТОЙ работы: ими владелец и ставил всё у себя, и
  // каждая проверена на его машине.
  const _ТС_КОМАНДЫ = [
    ['Разметка органов', 'pip install TotalSegmentator'],
    ['Счёт на видеокарте (иначе минуты вместо секунд)',
      'pip uninstall torch torchvision -y'],
    ['', 'pip install torch torchvision --index-url https://download.pytorch.org/whl/cu124'],
    ['Проверить, что видеокарта видна (ждём True)',
      'python -c "import torch; print(torch.cuda.is_available())"'],
    ['Разложить папку DICOM по сериям в .nii — нужно, если в исследовании есть серия с наклоном гентри',
      'pip install dcm2niix'],
    ['', 'mkdir "$env:USERPROFILE\\Desktop\\nii"'],
    ['', 'dcm2niix -o "$env:USERPROFILE\\Desktop\\nii" -z y "ПУТЬ_К_ПАПКЕ_DICOM"'],
  ];

  on('btn-totalseg-help', 'click', () => {
    const строки = _ТС_КОМАНДЫ.map(([что, кмд], i) => {
      const шапка = что ? `<div style="margin-top:12px; color:#8fa3c0;">${что}</div>` : '';
      return шапка + `<div style="display:flex; gap:6px; margin-top:4px; align-items:stretch;">`
        + `<code id="тс-кмд-${i}" style="flex:1; background:#0d1420; border:1px solid #334155; border-radius:4px;`
        + ` padding:6px 8px; font-size:11px; color:#7dd3fc; word-break:break-all;">${кмд}</code>`
        + `<button class="тс-копия" data-кмд="${i}" style="background:#1e293b; border:1px solid #334155;`
        + ` border-radius:4px; color:#c5c6c7; cursor:pointer; padding:0 10px; font-size:11px;">Копировать</button>`
        + `</div>`;
    }).join('');
    showResultModal('Что нужно установить для разметки органов', `
      <div style="color:#e0b070; margin-bottom:8px;">
        Это нужно ТОЛЬКО для кнопки «Разметить выбранные органы» — она считает полной
        моделью 1,5 мм и точнее. Кнопка «Разметить органы» выше работает без всякой
        установки: её модель уже внутри программы.
      </div>
      <div style="color:#8fa3c0;">Команды набираются в PowerShell. После установки
        перезапустите программу: она берёт список путей при запуске.</div>
      ${строки}
      <div style="margin-top:14px; color:#8fa3c0;">
        [11.1.84] Наклон гентри теперь обходится сам: если в папке есть такая серия,
        программа разложит папку по сериям этой же командой и предложит выбрать нужную.
        Для этого достаточно установить dcm2niix — команда выше. Вручную указать файл
        серии по-прежнему можно кнопкой «Выбрать файл серии».
      </div>
    `);
    // Копирование: своего буфера не заводим, берём браузерный.
    document.querySelectorAll('.тс-копия').forEach((б) => {
      б.addEventListener('click', async () => {
        const узел = document.getElementById(`тс-кмд-${б.dataset.кмд}`);
        const текст = узел?.textContent || '';
        try {
          await navigator.clipboard.writeText(текст);
          б.textContent = 'Скопировано';
          setTimeout(() => { б.textContent = 'Копировать'; }, 1500);
        } catch (e) {
          // Буфер бывает закрыт настройками — тогда хотя бы выделим текст,
          // чтобы человек скопировал сам, а не остался ни с чем.
          try {
            const r = document.createRange(); r.selectNodeContents(узел);
            const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
            б.textContent = 'Выделено — Ctrl+C';
          } catch (e2) { б.textContent = 'не вышло'; }
        }
      });
    });
  });

  // [11.1.80] ВЫБОР ОДНОГО ФАЙЛА СЕРИИ вместо папки.
  //
  // Владелец указал папку DICOM целиком и получил отказ: «Orthogonality check
  // failed: non cubical image», вектор [-0.198, -0.161, -0.967]. Это наклон
  // гентри — так снимают голову, и в его исследовании HEAD&BODY такая серия
  // есть. Программа разметки наклонённые серии не читает и, получив папку,
  // спотыкается на первой же.
  let _тсФайл = null;
  on('btn-totalseg-file', 'click', async () => {
    const мост = window.MikhmelDesktop;
    if (typeof мост?.pickNiftiFile !== 'function') {
      showToast('Выбор файла есть только в настольной сборке', 'warning', 5000);
      return;
    }
    const в = await мост.pickNiftiFile();
    if (!в) return;
    if (в.ошибка) { showToast('Файл не годится: ' + в.ошибка, 'warning', 5000); return; }
    _тсФайл = в.путь;
    const с = document.getElementById('totalseg-hod');
    if (с) с.textContent = `вход: ${в.имя} (${(в.размер / 1048576).toFixed(1)} МБ)`;
    log.info(`[TotalSeg] вход задан файлом: ${в.путь}`);
    showToast(`Вход задан: ${в.имя}. Теперь «Разметить выбранные органы»`, 'success', 5000);
  });

  // [11.1.77] РАЗМЕТКА ОРГАНОВ ЧЕРЕЗ TotalSegmentator (настольная сборка).
  //
  // Владелец делал это так: переключался в PowerShell, набирал команду, ждал,
  // возвращался и импортировал .nii по одному файлу. Работало, но переключение
  // туда-обратно на каждый орган.
  //
  // Браузер запускать программы на компьютере не может — это запрет в его
  // основе, а не наша недоделка. Поэтому кнопка живёт только в настольной
  // сборке, и в веб-версии говорит об этом прямо, а не молчит.
  //
  // Папку исследования страница не запоминает, поэтому спрашиваем её у
  // человека тем же выбором папки, которым открываются исследования.
  /**
   * [11.1.85] ТИКЕР ХОДА — ЧТОБЫ РАБОТА БЫЛА ВИДНА.
   *
   * ПОВОД, слова владельца: «идёт сообщение „раскладываю по сериям“ и пять минут
   * нет никакого прогресса. Как будто программа не работает».
   *
   * Он не мог знать, работает она или зависла: строка ставилась один раз. Здесь
   * две вещи разом, и нужны обе:
   *   · СЧЁТЧИК времени — он тикает всегда, даже если сама программа молчит. Это
   *     и есть доказательство, что мы живы;
   *   · ПЕРЕСКАЗ последней строки самой программы, если она что-то говорит.
   *     Приходит от оболочки через мост «наХод», см. electron/preload.cjs.
   *
   * Полоски с долей сделанного тут быть НЕ МОЖЕТ, и обещать её я не стану:
   * dcm2niix и TotalSegmentator не сообщают, сколько всего работы. Врать
   * нарисованным процентом хуже, чем честно тикать секундами.
   */
  let _тсТикер = null;
  let _тсОтписка = null;
  function _тсТикерПуск(показать, что) {
    _тсТикерСтоп();
    const начало = Date.now();
    let словами = '';
    const мост = window.MikhmelDesktop;
    if (typeof мост?.наХод === 'function') {
      _тсОтписка = мост.наХод((д) => { словами = String(д?.текст || '').slice(0, 120); });
    }
    const рисовать = () => {
      const с = Math.round((Date.now() - начало) / 1000);
      const время = `${Math.floor(с / 60)}:${String(с % 60).padStart(2, '0')}`;
      показать(`${что} — идёт ${время}${словами ? ' · ' + словами : ''}`);
    };
    рисовать();
    _тсТикер = setInterval(рисовать, 1000);
  }
  function _тсТикерСтоп() {
    if (_тсТикер) { clearInterval(_тсТикер); _тсТикер = null; }
    if (_тсОтписка) { try { _тсОтписка(); } catch (e) { /* уже отписаны */ } _тсОтписка = null; }
  }

  /**
   * [11.1.85] КАКАЯ СЕРИЯ ОТКРЫТА НА ЭКРАНЕ.
   *
   * Это и есть ответ на «что мне брать из таблицы»: размечать надо то, что врач
   * смотрит. Описание и номер серии лежат в DICOM, программа их знает — значит
   * спрашивать было нечего, и таблица из 830 строк была моей ленью, а не заботой
   * о его выборе.
   */
  function _тсОткрытаяСерия() {
    try {
      const re = getRenderingEngine();
      for (const vp of (re?.getViewports?.() || [])) {
        const ids = vp?.getImageIds?.();
        if (!ids?.length) continue;
        const м = cornerstone.metaData.get('generalSeriesModule', ids[0]) || {};
        if (м.seriesDescription || м.seriesNumber) {
          return { описание: м.seriesDescription || '', номер: м.seriesNumber || null, срезов: ids.length };
        }
      }
    } catch (e) { log.debug('[TotalSeg] открытую серию не определить:', e.message); }
    return {};
  }

  /**
   * [11.1.85] Список серий — КОРОТКИЙ, и с готовым ответом сверху.
   *
   * ЧТО БЫЛО В 11.1.84 И ПОЧЕМУ ЭТО НЕГОДНО. Я вывалил владельцу 830 строк,
   * отсортированных по размеру, с подписью «крупнее — не значит нужнее, поэтому
   * выбираем не мы». Он ответил: «что это за таблица, что мне брать из неё? Я
   * растерялся» — и закрыл окно. Правильно закрыл: это был не выбор, а отказ от
   * работы, оформленный как забота.
   *
   * И 830 — не 830 серий. Исследование двухэнергетическое: у одной серии десятки
   * файлов _e23 … _e72 (уровни энергии). Настоящих серий — десятки. Свёртка по
   * эхо и поиск открытой серии живут в core/niiSeriesPick.js и проверяются
   * числами на ЕГО списке имён.
   */
  function _тсПоказатьВыборСерии(отбор) {
    const строка = (ф, советуем) => {
      const мб = ((ф.размер || 0) / 1048576).toFixed(1);
      const эхо = ф.эхоВсего > 1 ? ` · уровней энергии ${ф.эхоВсего}` : '';
      return `<div style="display:flex; gap:6px; margin-top:6px; align-items:center;">`
        + `<button class="тс-серия" data-путь="${encodeURIComponent(ф.путь)}" data-имя="${encodeURIComponent(ф.имя)}"`
        + ` style="background:${советуем ? '#1d4ed8' : '#1e293b'}; border:1px solid #334155;`
        + ` border-radius:4px; color:#fff; cursor:pointer; padding:4px 10px; font-size:11px;">Взять</button>`
        + `<span style="flex:1; font-size:11px; color:#c5c6c7; word-break:break-all;">${ф.имя}</span>`
        + `<span style="font-size:11px; color:#8fa3c0; white-space:nowrap;">${мб} МБ${эхо}</span></div>`;
    };
    const шапка = отбор.точные.length
      ? `<div style="color:#7dd3fc; margin-bottom:6px;">Вот серия, которую вы смотрите — сошлась `
        + `${отбор.почему}. Скорее всего нужна она:</div>` + отбор.точные.map((ф) => строка(ф, true)).join('')
        + `<div style="color:#8fa3c0; margin:10px 0 0;">Если нужна другая — остальные серии ниже.</div>`
      : `<div style="color:#e0b070; margin-bottom:6px;">Открытую серию сопоставить с файлами не удалось,`
        + ` поэтому выбирать придётся по названию. Ищите серию живота (Body), а не головы (Head)`
        + ` и не служебную (Monitoring, Topogram).</div>`;
    const прочие = отбор.прочие.slice(0, 15).map((ф) => строка(ф, false)).join('');
    showResultModal('Какую серию размечать', `
      <div style="color:#8fa3c0; margin-bottom:8px;">
        В папке ${отбор.серий} серий (файлов ${отбор.файлов}: у двухэнергетического
        исследования одна серия даёт десятки файлов на разных уровнях энергии — они свёрнуты
        в одну строку).
      </div>
      ${шапка}
      ${прочие}
      ${отбор.прочие.length > 15 ? `<div style="color:#8fa3c0; margin-top:8px;">Показаны 15 самых крупных из ${отбор.прочие.length}. Если нужной здесь нет — «Указать серию файлом .nii».</div>` : ''}`);
    // Кнопки живут в окне справки, поэтому и слушаем окно, а не панель.
    setTimeout(() => {
      document.querySelectorAll('.тс-серия').forEach((б) => {
        б.addEventListener('click', () => {
          const путь = decodeURIComponent(б.dataset.путь || '');
          const имя = decodeURIComponent(б.dataset.имя || '');
          if (!путь) return;
          _тсФайл = путь;
          const с = document.getElementById('totalseg-hod');
          if (с) с.textContent = `вход: ${имя} — нажмите «Разметить выбранные органы»`;
          log.info(`[TotalSeg] серия выбрана: ${путь}`);
          showToast(`Серия выбрана: ${имя}. Нажмите «Разметить выбранные органы»`, 'success', 8000);
        }, { signal: _ac.signal });
      });
    }, 50);
  }

  /**
   * [11.1.87] Черновики этого запуска — чтобы убрать за собой.
   *
   * Слова владельца: «рядом с каталогом DICOM формируется каталог seg, в котором
   * очень много файлов, и что мне с ними дальше делать? Я то был уверен, что
   * результатом работы будет автоматическая сегментация органов, а не набор
   * файлов».
   *
   * Он прав дважды. Результат работы — маска в сегментах, и она вносится; файлы
   * — средство. А раскладка папки по сериям вообще писала гигабайты рядом с
   * исследованием и ничего за собой не убирала.
   */
  const _черновики = new Set();

  async function _тсУбратьЧерновики(мост) {
    if (typeof мост?.убратьПапку !== 'function' || !_черновики.size) return null;
    let файлов = 0, мб = 0, папок = 0;
    for (const п of Array.from(_черновики)) {
      try {
        const р = await мост.убратьПапку(п);
        if (р?.ок) { папок++; файлов += р.файлов || 0; мб += р.мб || 0; _черновики.delete(п); }
        else log.debug(`[TotalSeg] не убрать ${п}: ${р?.причина}`);
      } catch (e) { log.debug('[TotalSeg] уборка:', e.message); }
    }
    return папок ? { папок, файлов, мб: Math.round(мб * 10) / 10 } : null;
  }

  /**
   * [11.1.91] Размеры и шаг ОТКРЫТОГО ИССЛЕДОВАНИЯ — у самого окна.
   *
   * Не у холста маски: холста может ещё не быть (он создаётся при первом
   * импорте), и в 11.1.90 сверка сеток из-за этого не срабатывала ни разу,
   * молча уходя в отладку. Окно с томом есть всегда, когда исследование открыто.
   */
  function _тсРазмерыИсследования() {
    try {
      const re = getRenderingEngine();
      for (const vp of (re?.getViewports?.() || [])) {
        if (vp?.type !== 'orthographic') continue;
        const и = vp.getImageData?.();
        const d = и?.dimensions; const s = и?.spacing;
        if (d?.length === 3 && d[0] > 0 && d[1] > 0 && d[2] > 0) {
          return { dims: Array.from(d), spacing: Array.from(s || [1, 1, 1]) };
        }
      }
    } catch (e) { log.debug('[TotalSeg] размеры исследования не получить:', e.message); }
    return null;
  }

  /**
   * [11.1.89] В КАКИХ ОКНАХ МАСКА ВИДНА, А В КАКИХ ЕЁ НЕТ.
   *
   * Холст маски цепляется только к срезовым окнам (orthographic) — так устроено
   * в toolManager, и это не оплошность: в объёмном окне кистью не рисуют, в
   * стопке снимков холста нет вовсе.
   *
   * Но врач об этом знать не обязан. «Нативные срезы» — это стопка, и маска там
   * не появится никогда, сколько ни размечай. Владелец смотрел именно туда и
   * спросил: «что я делаю неправильно?» — ничего. Молчала программа.
   */
  function _тсГдеВиднаМаска() {
    const срезовые = []; let есть_стопка = false; let фокус = null;
    try {
      const re = getRenderingEngine();
      for (const vp of (re?.getViewports?.() || [])) {
        if (vp?.type === 'orthographic') срезовые.push(vp.id);
        else if (vp?.type === 'stack') есть_стопка = true;
      }
      фокус = window.MikhmelViewer?.getCurrentViewportId?.() || null;
    } catch (e) { log.debug('[Маска] окна не опросить:', e.message); }
    const фокусСрезовое = фокус ? срезовые.includes(фокус) : null;
    return {
      окна: срезовые,
      // Предупреждаем, когда стопка есть, а в фокусе НЕ срезовое окно, — или
      // когда срезовых окон нет вовсе: тогда маску не увидеть нигде.
      предупредить: !срезовые.length || (есть_стопка && фокусСрезовое === false),
    };
  }

  let _тсИдёт = false;
  on('btn-totalseg', 'click', async () => {
    const строка = document.getElementById('totalseg-hod');
    const кнопка = document.getElementById('btn-totalseg');
    const показать = (тт) => { if (строка) строка.textContent = тт; };
    const мост = window.MikhmelDesktop;
    if (!мост?.isDesktop || typeof мост.runTotalSegmentator !== 'function') {
      showToast('Эта кнопка работает только в настольной сборке: браузеру запрещено запускать '
        + 'программы на компьютере. В веб-версии размечайте командой TotalSegmentator и вносите '
        + 'файлы через «Импорт маски»', 'warning', 12000);
      return;
    }
    if (_тсИдёт) { showToast('Разметка уже идёт — дождитесь конца', 'info', 2500); return; }

    const органы = Array.from(document.querySelectorAll('.ts-organ:checked')).map((э) => э.value);
    if (!органы.length) { showToast('Отметьте хотя бы один орган', 'info', 3000); return; }

    _тсИдёт = true;
    if (кнопка) кнопка.disabled = true;
    try {
      // [11.1.80] Если задан файл серии — берём его, папку не спрашиваем.
      let вход = _тсФайл;
      let выходКорень = null;
      if (вход) {
        выходКорень = вход.replace(/[^\\/]+$/, '') + 'mikhmel-seg';
        показать('вход задан файлом — считаю');
      } else {
        показать('выберите папку с DICOM этого исследования');
        const выбор = await мост.pickStudyFolder();
        if (!выбор?.dir) { показать(''); return; }
        вход = выбор.dir;
        выходКорень = `${выбор.dir}\\mikhmel-seg`;
      }

      // ══════════════════════════════════════════════════════════════
      // [11.1.91] СНАЧАЛА ОТБИРАЕМ ФАЙЛЫ ОТКРЫТОЙ СЕРИИ — И ТОЛЬКО ИХ.
      //
      // Слова владельца: «я выделил органы брюшной полости, значит нам не нужна
      // библиотека dcm2niix». Именно так. Наклон гентри был у серии ГОЛОВЫ, а
      // размечался ЖИВОТ; сторонняя программа спотыкалась о чужую серию, а я из
      // этого делал вывод про всю папку и гнал её через dcm2niix целиком.
      //
      // Правильный вход — папка из файлов ОДНОЙ серии, той, что на экране. Тогда
      // ни наклона (его в этой серии нет), ни раскладки, ни выбора из 830
      // кусков, ни гигабайта черновика. Файлы отбираются по SeriesInstanceUID и
      // кладутся ЖЁСТКИМИ ССЫЛКАМИ — места это не занимает.
      //
      // Если отбор не удался (нет двери, серия не определилась, файлов не
      // нашлось) — работаем по-старому, всей папкой: хуже, но не отказ.
      // ══════════════════════════════════════════════════════════════
      if (!_тсФайл && typeof мост.собратьСерию === 'function') {
        const uid = window.MikhmelViewer?.getCurrentSeries?.();
        if (!uid) {
          log.warn('[TotalSeg] какая серия открыта — неизвестно, беру папку целиком');
        } else {
          показать('отбираю файлы открытой серии');
          const сб = await мост.собратьСерию({ вход, uid });
          if (сб?.ок) {
            _черновики.add(сб.папка);
            log.info(`[TotalSeg] отобрано ${сб.файлов} файлов серии ${uid} `
              + `(просмотрено ${сб.просмотрено}, серий в папке ${сб.серий}) → ${сб.папка}`);
            вход = сб.папка;
          } else {
            log.warn(`[TotalSeg] отбор серии не удался: ${сб?.причина}`);
            showToast('Не удалось отобрать файлы открытой серии, считаю по всей папке. '
              + 'Причина: ' + (сб?.причина || 'неизвестна'), 'warning', 12000);
          }
        }
      }

      log.info(`[TotalSeg] запуск: ${вход} → органы ${органы.join(', ')}`);
      // [11.1.85] Было: одна неподвижная строка «это секунды на видеокарте».
      // На процессоре это минуты, и неподвижная строка означает для человека
      // «программа висит».
      _тсТикерПуск(показать, `считаю ${органы.length} орган(ов)`);
      let р = await мост.runTotalSegmentator({ вход, выход: выходКорень, органы });
      _тсТикерСтоп();

      // [11.1.84] НАКЛОН ГЕНТРИ — НЕ ТУПИК. Раскладываем папку по сериям сами.
      //
      // Владелец сообщил «не работает» ВТОРОЙ раз. Прежде программа объясняла
      // причину верными словами и посылала его в PowerShell: поставь dcm2niix,
      // набери команду, найди файл, вернись, нажми «Выбрать файл серии». Работа,
      // которую человек делает руками по нашей подсказке, должна делаться
      // программой. Ту же команду набираем мы.
      if (!р?.ок && р?.наклон && typeof мост.dicomToNifti === 'function' && !_тсФайл) {
        // [11.1.85] Раскладка идёт минутами. Тикер обязателен: молчащая строка
        // неотличима от зависшей программы — владелец так и написал, «как будто
        // программа не работает».
        _тсТикерПуск(показать, 'раскладываю серию через dcm2niix');
        // [11.1.91] Сюда доходим ТОЛЬКО если наклонена сама размечаемая серия:
        // чужие серии уже отсеяны отбором по номеру серии выше. Прежде этот путь
        // запускался на всю папку — отсюда и были 830 кусков.
        log.info(`[TotalSeg] наклон гентри в самой серии: раскладываю ${вход} через dcm2niix`);
        // [11.1.87] Черновик — в служебную папку системы, а не рядом с
        // исследованием. Прежде здесь появлялись гигабайты .nii рядом с DICOM, и
        // владелец справедливо спросил, что ему с ними делать.
        let папкаСерий = выходКорень + '-серии';
        try {
          const ч = await мост.папкаЧерновика?.('серии');
          if (ч?.путь) папкаСерий = ч.путь;
        } catch (e) { log.debug('[TotalSeg] служебная папка недоступна, кладу рядом'); }
        _черновики.add(папкаСерий);
        const рз = await мост.dicomToNifti({ вход, выход: папкаСерий });
        _тсТикерСтоп();
        if (!рз?.ок) {
          показать('');
          log.warn('[TotalSeg] dcm2niix не вышло:', рз?.причина);
          showToast('Разложить папку по сериям не удалось: ' + (рз?.причина || 'неизвестная беда')
            + (рз?.нетПрограммы ? '' : ' Можно указать файл серии вручную — кнопка «Выбрать файл серии».'),
            'warning', 15000);
          return;
        }
        // [11.1.85] Сначала СВОРАЧИВАЕМ эхо и ищем ту серию, которая открыта на
        // экране. У владельца dcm2niix выдал 830 файлов — это не 830 серий, а
        // десятки серий двухэнергетического исследования, каждая на десятках
        // уровней энергии. Прежде я показывал все 830 и просил выбрать; он
        // растерялся и закрыл окно, и был прав.
        const { отобрать } = await import('../core/niiSeriesPick.js');
        const открытая = _тсОткрытаяСерия();
        const отбор = отобрать(рз.файлы, открытая);
        log.info(`[TotalSeg] файлов ${отбор.файлов}, серий ${отбор.серий}; `
          + `открыта «${открытая.описание || '?'}» №${открытая.номер ?? '?'}; `
          + `сошлось ${отбор.точные.length}${отбор.почему ? ' ' + отбор.почему : ''}`);

        // ══════════════════════════════════════════════════════════════
        // [11.1.90] СВЕРЯЕМ СЕТКУ КАНДИДАТА С ИССЛЕДОВАНИЕМ — ДО СЧЁТА.
        //
        // ПОВОД — журнал владельца, и здесь наконец есть решающее число:
        //     файл «liver.nii.gz» (0.1 МБ) прочитан, сетка 512×512×95
        // при исследовании 512×512×808. То есть разметка считалась по объёму в
        // ДЕВЯНОСТО ПЯТЬ срезов — меньше восьмой части тела. Печени и селезёнки
        // в этом куске просто не было, сеть честно вернула пустоту.
        //
        // ЧЕМ Я БЫЛ НЕПРАВ ДВАЖДЫ. В 11.1.85 я выбирал среди эхо самый крупный
        // файл и написал «уровень энергии влияет слабо — это допущение». В
        // 11.1.89, увидев суффикс _e24, я решил, что дело в единицах Хаунсфилда,
        // и добавил предупреждение про двухэнергетическую реконструкцию. Обе
        // догадки мимо: дело в том, что dcm2niix разложил серию на куски, и мой
        // отбор брал кусок, а не серию.
        //
        // ПОЧЕМУ РАЗМЕР НЕ ГОДИТСЯ В КАЧЕСТВЕ МЕРЫ, а число срезов годится:
        // 25 МБ сжатого — это и 818 срезов обычного КТ, и 95 срезов чего-то
        // плотного. Сжатие про содержимое ничего не обещает. Число срезов в
        // заголовке — не догадка, а запись.
        //
        // Читаем ЗАГОЛОВОК (348 байт после распаковки) у кандидатов по очереди и
        // берём первого, чья сетка сходится с открытым исследованием. Пять
        // попыток: если и там не сошлось, считать бессмысленно, и надо сказать
        // это, а не тратить минуты на заведомо пустой ответ.
        // ══════════════════════════════════════════════════════════════
        const кандидаты = [...отбор.точные, ...отбор.прочие].slice(0, 5);
        let годный = null; const отвергнуты = [];
        try {
          const { разобратьЗаголовок, сеткиСовпадают, этоGzip } = await import('../core/niftiRead.js');
          // [11.1.91] РАЗМЕРЫ ИССЛЕДОВАНИЯ БЕРЁМ У ОКНА, А НЕ У ХОЛСТА МАСКИ.
          //
          // В 11.1.90 я спрашивал их у томМаски() — а холст маски в этот момент
          // ЕЩЁ НЕ СОЗДАН: в журнале владельца он появляется позже, строкой
          // «холста нет — готовлю». Сверка падала на первой же строке, вылетала
          // в тихий log.debug, и разметка шла дальше как ни в чём не бывало.
          // При этом плашка говорила «Сетка с исследованием сошлась» — фраза
          // была вписана жёстко, без всякой проверки. Третья моя плашка подряд,
          // утверждавшая то, чего не проверяла.
          let dimsТома = null; let spТома = null;
          const и = _тсРазмерыИсследования();
          if (и) { dimsТома = и.dims; spТома = и.spacing; }
          if (!dimsТома) throw new Error('размеры исследования недоступны');
          for (const к of кандидаты) {
            показать(`сверяю сетку: ${к.имя}`);
            let з = null;
            try {
              let буф = await мост.readFile(к.путь);
              if (!(буф instanceof ArrayBuffer)) буф = буф?.buffer || буф;
              // dcm2niix пишет .nii.gz — распаковываем средствами браузера, тем
              // же способом, что и импорт маски: своей библиотеки ради одного
              // формата не заводим.
              if (этоGzip(буф)) {
                if (typeof DecompressionStream !== 'function') {
                  отвергнуты.push(`${к.имя}: браузер не распаковывает .gz`);
                  continue;
                }
                const поток = new Blob([буф]).stream().pipeThrough(new DecompressionStream('gzip'));
                буф = await new Response(поток).arrayBuffer();
              }
              з = разобратьЗаголовок(буф);
            } catch (eЗ) { отвергнуты.push(`${к.имя}: не прочитать (${eЗ.message})`); continue; }
            if (!з?.ок) { отвергнуты.push(`${к.имя}: ${з?.причина || 'заголовок не разобран'}`); continue; }
            const св = сеткиСовпадают(з.dims, з.spacing, dimsТома, spТома);
            if (св.ок) { годный = { ...к, dims: з.dims }; break; }
            отвергнуты.push(`${к.имя}: ${з.dims.join('×')} против ${dimsТома.join('×')}`);
            log.warn(`[TotalSeg] кандидат отвергнут — ${к.имя}: сетка ${з.dims.join('×')}, `
              + `у исследования ${dimsТома.join('×')}`);
          }
        } catch (eС) { log.debug('[TotalSeg] сверку сеток не выполнить:', eС.message); }

        if (годный) {
          отбор.лучшая = годный;
          отбор.точные = [годный];
          log.info(`[TotalSeg] сетка сошлась: ${годный.имя} — ${годный.dims.join('×')}`);
        } else if (отвергнуты.length) {
          показать('ни один файл не совпал с исследованием по сетке');
          log.warn(`[TotalSeg] ни один кандидат не подошёл: ${отвергнуты.join('; ')}`);
          showToast('Ни один из полученных файлов серий НЕ совпадает с открытым исследованием'
            + ` по числу срезов. Проверено ${отвергнуты.length}: ${отвергнуты.slice(0, 3).join('; ')}.`
            + ' Считать по такому файлу бессмысленно — разметка вышла бы пустой, как в прошлый раз.'
            + ' Скорее всего dcm2niix разложил серию на куски (двухэнергетическое исследование).'
            + ' Укажите файл серии вручную кнопкой «Указать серию файлом .nii»', 'error', 25000);
          return;
        }

        // [11.1.89] Предупреждаем ДО счёта, а не после пустого ответа: у
        // владельца сеть отработала 16 секунд по двухэнергетической
        // реконструкции и вернула пустой объём.
        // [11.1.90] Предупреждение осталось, но БЕЗ прежней догадки про единицы
        // Хаунсфилда: журнал показал, что дело было в другом — файл содержал
        // 95 срезов вместо 808. Сетку теперь сверяем выше, а здесь только
        // называем то, что видно из имени: это не обычная реконструкция.
        if (отбор.двухэнергетическая) {
          log.warn(`[TotalSeg] выбран файл с суффиксом эха (${отбор.лучшая?.имя}) — `
            + 'обычной реконструкции для этой серии не нашлось');
          showToast('У этой серии только реконструкции с суффиксом _e (двухэнергетическое'
            + ' исследование)' + (годный ? `, сетка сошлась: ${годный.dims.join('×')}` : '')
            + '. Считаю — но если органов не появится, попробуйте другую серию кнопкой'
            + ' «Указать серию файлом .nii»', 'warning', 15000);
        }
        if (отбор.лучшая && отбор.точные.length === 1) {
          // Сошлась ровно одна серия — та, что на экране. Спрашивать нечего.
          _тсФайл = отбор.лучшая.путь;
          log.info(`[TotalSeg] беру открытую серию: ${отбор.лучшая.имя}`);
          showToast(`Размечаю серию, которую вы смотрите: ${отбор.лучшая.имя} `
            + `(сошлась ${отбор.почему}). Не та? Остановите и нажмите «Указать серию файлом .nii»`,
            'info', 10000);
          _тсТикерПуск(показать, `считаю по серии ${отбор.лучшая.имя}`);
          р = await мост.runTotalSegmentator({ вход: _тсФайл, выход: выходКорень, органы });
          _тсТикерСтоп();
        } else if (отбор.серий === 1) {
          // Серия в папке одна — выбирать нечего.
          _тсФайл = отбор.прочие[0]?.путь || рз.файлы[0].путь;
          _тсТикерПуск(показать, 'серия одна — считаю');
          р = await мост.runTotalSegmentator({ вход: _тсФайл, выход: выходКорень, органы });
          _тсТикерСтоп();
        } else {
          // Сошлось несколько или ничего — показываем КОРОТКИЙ список, а не 830
          // строк, и советуем сверху ту, что на экране.
          показать('выберите серию');
          _тсПоказатьВыборСерии(отбор);
          return;
        }
      }

      if (!р?.ок) {
        показать('');
        log.warn('[TotalSeg] не вышло:', р?.причина, р?.вывод ? '| вывод: ' + р.вывод : '');
        showToast('TotalSegmentator: ' + (р?.причина || 'неизвестная беда')
          + (р?.вывод ? '\n\n' + р.вывод : ''), 'error', 15000);
        return;
      }
      log.info(`[TotalSeg] готово за ${(р.мс / 1000).toFixed(0)} с, файлов ${р.файлы.length}`);
      показать(`готово за ${(р.мс / 1000).toFixed(0)} с — вношу маски`);

      // Маски вносим ТЕМ ЖЕ путём, которым идёт «Импорт маски»: разворот
      // ориентации, сверка с открытым исследованием, запоминание правки. Своей
      // дороги не заводим — иначе разошлись бы две.
      // [11.1.87] ИМЕНА ПО-РУССКИ И НОМЕР СЕГМЕНТА. Прежде в строке хода
      // появлялось «spleen» — английское слово в русском интерфейсе, и главное:
      // врач не видел, В КАКОЙ сегмент это легло. Результат работы — маска в
      // сегменте, и назвать его надо прямо.
      const { имяОргана } = await import('../ai/organsSeg.js').catch(() => ({ имяОргана: (и) => и }));
      // [11.1.89] СЧИТАЕМ ВОКСЕЛИ, А НЕ ФАКТ ПОДАЧИ ФАЙЛА.
      //
      // ПОВОД — владелец: «я выбрал селезёнку, я не вижу, чтобы селезёнка была
      // выделена, а ты? что я делаю неправильно?» — и приложил мою же плашку
      // «Разметка внесена в сегменты за 16 с: селезёнка → сегмент 1».
      //
      // Он не делал ничего неправильно. ВРАЛА ПЛАШКА. Смотрите, как было
      // устроено: мы подсовывали файл в поле выбора маски и объявляли «внесено»
      // сразу после подачи. Импорт живёт своей жизнью — он слушает событие
      // change и может выйти по любой из своих проверок (пустой файл, сетка не
      // сошлась, ориентация не приводится) молча для нас: у него свой return, не
      // бросок. То есть «внесено» означало «файл подан», а не «в маске появились
      // воксели». Слово было моё, проверки за ним не стояло никакой.
      //
      // Теперь считаем ДО и ПОСЛЕ. Появились воксели — так и говорим, с числом и
      // объёмом. Не появились — говорим это, а не «внесено».
      const { томМаски } = await import('../core/organView.js');
      const { подсчётВокселей, объёмМл } = await import('../core/organMath.js');
      const сколькоВ = (индекс) => {
        try {
          const м = томМаски();
          return м.ошибка ? null : { n: подсчётВокселей(м.пиксели, индекс), spacing: м.spacing };
        } catch (e) { return null; }
      };

      const внесено = []; const неВнесено = []; const пустые = [];

      // ══════════════════════════════════════════════════════════════
      // [12.0.0-правка] ОСТАТКИ ПРОШЛОГО ЗАПУСКА УБИРАЕМ ИЗ САМИХ СЕГМЕНТОВ.
      //
      // ВТОРАЯ БЕДА С ТЕХ ЖЕ СНИМКОВ ВЛАДЕЛЬЦА, и она тише первой. Аорта:
      //     размечено в файле 513 060 вокселей
      //     добавлено в сегмент 1:      14 773
      //     объём сегмента:              234,6 мл
      // Внесённые 14 773 вокселя — это 6,5 мл. Значит в сегменте 1 уже лежало
      // около 228 мл от ПРОШЛОГО запуска, и почти вся аорта (498 287 вокселей,
      // 97%) не записалась: импорт правильно не затирает чужие сегменты, а весь
      // объём аорты был уже занят прежними масками.
      //
      // В 12.0.0 я убрал старые ФАЙЛЫ масок из папки результата — и решил, что
      // этого хватит. Не хватило: старые маски остались В САМИХ СЕГМЕНТАХ
      // программы, и новый запуск бился о них.
      //
      // ПОЧЕМУ ЭТО МОЖНО ЧИСТИТЬ. Врач нажал «разметить выбранные органы» —
      // он просит новую разметку, а не дополнение к прежней. Сегменты 1..N
      // отводятся под неё.
      // ПОЧЕМУ ЭТО ВСЁ РАВНО ОПАСНО, и потому под отмену. В одном из этих
      // сегментов могла лежать РУЧНАЯ работа. Поэтому: чистим одной ступенью
      // отмены (одно нажатие «Вернуть маску» возвращает всё как было) и
      // называем врачу число убранных вокселей, а не молчим.
      // ══════════════════════════════════════════════════════════════
      try {
        const целевые = new Set(р.файлы.map((_, i) => i + 1));
        const м = томМаски();
        if (!м.ошибка) {
          const индексы = []; const прежние = [];
          for (let i = 0; i < м.пиксели.length; i++) {
            if (целевые.has(м.пиксели[i])) { индексы.push(i); прежние.push(м.пиксели[i]); }
          }
          if (индексы.length) {
            const { запомнитьПравкуМаски } = await import('../core/organView.js');
            const { записатьМаску } = await import('../core/organView.js');
            запомнитьПравкуМаски(индексы, null, 'очистка перед разметкой',
              Uint8Array.from(прежние));
            for (const и of индексы) м.пиксели[и] = 0;
            записатьМаску(м.пиксели);
            log.info(`[TotalSeg] убрано из сегментов 1…${р.файлы.length} остатков: `
              + `${индексы.length.toLocaleString('ru-RU')} вокселей (одна ступень отмены)`);
            showToast(`В сегментах 1…${р.файлы.length} уже было `
              + `${индексы.length.toLocaleString('ru-RU')} вокселей — убрал, чтобы новая разметка `
              + 'не билась об остатки прошлого запуска. Одно нажатие «Вернуть маску» вернёт как было',
              'info', 12000);
          }
        }
      } catch (eО) { log.warn('[TotalSeg] очистить сегменты не вышло:', eО.message); }

      for (let i = 0; i < р.файлы.length; i++) {
        const ф = р.файлы[i];
        const структура = ф.имя.replace(/\.nii(\.gz)?$/, '');
        const порусски = имяОргана(структура);
        const сегмент = i + 1;
        показать(`вношу ${i + 1} из ${р.файлы.length}: ${порусски} → сегмент ${сегмент}`);
        try {
          if (!_тсВыбратьСегмент(сегмент)) {
            // [12.0.0-правка] Не смогли встать на нужный сегмент — НЕ ПИШЕМ. Прежде
            // возврат false никто не проверял, и маска ложилась в чужой
            // сегмент: у владельца печень, поджелудочная и селезёнка слились в
            // сегменте 5, и разделить их уже нельзя.
            неВнесено.push(порусски);
            log.warn(`[TotalSeg] ${порусски}: сегмент ${сегмент} недоступен — пропускаю, `
              + 'чтобы не смешать с чужой маской');
            continue;
          }
          const было = сколькоВ(сегмент);
          const буфер = await мост.readFile(ф.путь);
          await _тсПодатьВИмпорт(буфер, ф.имя);
          // Импорт делает свою работу и рисует свой итог; ждём, чтобы файлы не
          // пошли внахлёст по одному холсту.
          await new Promise((r) => setTimeout(r, 1200));
          const стало = сколькоВ(сегмент);
          const прибыло = (стало?.n ?? 0) - (было?.n ?? 0);
          if (прибыло > 0) {
            const мл = стало ? объёмМл(томМаски().пиксели, сегмент, стало.spacing) : 0;
            внесено.push(`${порусски} → сегмент ${сегмент} (${мл.toFixed(0)} мл)`);
            log.info(`[TotalSeg] ${порусски} → сегмент ${сегмент}: `
              + `+${прибыло.toLocaleString('ru-RU')} вокселей, ${мл.toFixed(1)} мл`);
          } else {
            пустые.push(порусски);
            log.warn(`[TotalSeg] ${порусски}: файл подан, но в сегменте ${сегмент} `
              + 'не прибавилось ни одного вокселя — импорт вышел по своей проверке');
          }
        } catch (eф) {
          неВнесено.push(порусски);
          log.warn(`[TotalSeg] ${ф.имя}:`, eф.message);
        }
      }

      // [11.1.87] УБИРАЕМ ЗА СОБОЙ. Черновик серий — гигабайты .nii — больше не
      // нужен: маски уже в сегментах. Папку с масками НЕ удаляем сами: врач мог
      // захотеть отдать их коллеге, и решать это за него нельзя. Но говорим, где
      // она и что она не нужна.
      const убрано = await _тсУбратьЧерновики(мост);
      if (убрано) {
        log.info(`[TotalSeg] убрано черновиков: папок ${убрано.папок}, файлов ${убрано.файлов}, ${убрано.мб} МБ`);
      }

      // [11.1.88] ПУСТЫЕ МАСКИ — ЭТО НЕ «ГОТОВО».
      //
      // Живой случай владельца: он указал входом C:\...\Downloads\печень.nii —
      // а это файл МАСКИ, выгруженный из нашей же программы, а не серия КТ.
      // TotalSegmentator честно посчитал по нему и выдал четыре файла, в которых
      // размечать было нечего; строки с органами пробежали, «готово» сказалось,
      // а на снимке не появилось ничего. Молчание тут хуже отказа: врач ищет
      // ошибку в нашей программе, а дело во входе.
      //
      // Проверяем не «сколько файлов», а сколько вокселей внеслось: пустой файл
      // импорт пропускает, и в «внесено» он не попадает.
      if (пустые.length && !внесено.length) {
        показать(`ни одного вокселя: ${пустые.join(', ')}`);
        showToast(`Файлы разметки получены (${р.файлы.length}), но в маске НЕ ПРИБАВИЛОСЬ`
          + ' ни одного вокселя — значит импорт вышел по своей проверке и ничего не записал.'
          + ' Точную причину он написал в журнал: чаще всего это несовпадение сетки (маска от'
          + ' другой серии) или пустой файл (на вход дана не серия КТ, а, например, ранее'
          + ' выгруженная маска). Откройте журнал: строка «[Импорт маски]» скажет, что именно',
          'error', 25000);
        return;
      }
      if (пустые.length) {
        log.warn(`[TotalSeg] без вокселей остались: ${пустые.join(', ')}`);
      }
      if (!внесено.length && р.файлы.length) {
        показать('маски пустые — вход, похоже, не серия КТ');
        showToast(`Разметка отработала и выдала ${р.файлы.length} файлов, но НИ В ОДНОМ нет`
          + ' размеченных вокселей. Так бывает, когда на вход дана не серия КТ, а другой файл —'
          + ' например, ранее выгруженная МАСКА (.nii с одним органом) или снимок другого типа.'
          + ' Проверьте, что указана серия исследования: нажмите «Указать серию файлом .nii»'
          + ' и выберите файл, полученный из DICOM, либо ничего не указывайте — тогда серию'
          + ' возьмём из открытого исследования сами', 'error', 25000);
        return;
      }
      показать(внесено.length
        ? `готово: ${внесено.join('; ')}`
        : 'маски получены, но внести не удалось');
      // [11.1.89] ГДЕ МАСКА ВИДНА, А ГДЕ ЕЁ НЕТ ВОВСЕ.
      //
      // Второй возможный ответ на «я не вижу, чтобы селезёнка была выделена».
      // Маска цепляется ТОЛЬКО к срезовым окнам (orthographic) — так устроен
      // холст, см. toolManager: «маска в окнах: 3 из 3 (axial, sagittal,
      // coronal)». В окне «Нативные срезы» это стопка (stack), и маски там нет
      // и не будет. Врач смотрит в стопку, маска лежит в срезовых окнах — и
      // программа об этом молчала.
      const гдеВидно = _тсГдеВиднаМаска();
      const хвост = (гдеВидно.предупредить
        ? ` ВНИМАНИЕ: маска рисуется только в срезовых окнах${гдеВидно.окна.length ? ` (${гдеВидно.окна.join(', ')})` : ''}`
          + ', а в «Нативных срезах» её нет вовсе — это стопка снимков, холст маски к ней не цепляется.'
          + ' Переключитесь на раскладку MPR, если сейчас смотрите нативные срезы.'
        : '')
        + (убрано ? ` Черновик убран (${убрано.файлов} файлов, ${убрано.мб} МБ).` : '')
        + ` Готовые маски лежат файлами в папке mikhmel-seg рядом с исследованием — они больше не нужны,`
        + ' в программу уже внесены; папку можно удалить.'
        + (неВнесено.length ? ` Не удалось внести: ${неВнесено.join(', ')} — внесите через «Импорт маски».` : '');
      showToast(внесено.length
        ? `Разметка внесена в сегменты за ${(р.мс / 1000).toFixed(0)} с: ${внесено.join('; ')}.${хвост}`
        : `Маски посчитаны (${р.файлы.length} файлов) в папке mikhmel-seg, но внести их не удалось — `
          + 'внесите вручную через «Импорт маски»', внесено.length ? 'success' : 'warning', 20000);
    } catch (e) {
      показать('');
      log.warn('[TotalSeg] сорвалось:', e.message);
      showToast('TotalSegmentator: ' + e.message, 'error', 9000);
    } finally {
      // Тикер останавливаем ЗДЕСЬ тоже: если счёт сорвался бросанием, он иначе
      // тикал бы вечно и врал, что работа идёт.
      _тсТикерСтоп();
      // [11.1.87] И черновик убираем даже когда сорвалось: иначе после каждой
      // неудачной попытки на диске оставались бы гигабайты, о которых никто не
      // знает. Ошибку уборки не показываем — она не про работу врача.
      _тсУбратьЧерновики(window.MikhmelDesktop).catch(() => {});
      _тсИдёт = false;
      if (кнопка) кнопка.disabled = false;
    }
  });

  // [11.1.65] ПЕЧЕНЬ ЦЕЛИКОМ. Кнопка одна, точек не требует. Двойного запуска
  // не допускаем: прогон идёт около минуты, и второй поверх первого испортил бы
  // маску обоими сразу.
  let _печеньИдёт = false;
  on('btn-liver-auto', 'click', async () => {
    if (_печеньИдёт) { showToast('Печень уже считается — дождитесь конца', 'info', 2500); return; }
    const кнопка = document.getElementById('btn-liver-auto');
    const строка = document.getElementById('liver-auto-hod');
    const показать = (т) => { if (строка) строка.textContent = т; };
    _печеньИдёт = true;
    if (кнопка) { кнопка.disabled = true; }
    try {
      const { печеньАвтоматом } = await import('../ai/liverSeg.js');
      const р = await печеньАвтоматом({
        сегмент: _активныйНомерСегмента(),
        ход: (доля, текст) => показать(`${Math.round(доля * 100)}% · ${текст}`),
      });
      if (!р.ок) {
        показать('');
        log.warn('[Печень] не вышло:', р.причина);
        showToast('Печень не посчиталась: ' + р.причина, 'warning', 9000);
        return;
      }
      показать(`${р.мл.toFixed(0)} мл за ${(р.мс / 1000).toFixed(0)} с`);
      showToast(`Печень найдена: ${р.мл.toFixed(1)} мл, срезов ${р.срезов}, `
        + `за ${(р.мс / 1000).toFixed(0)} с. Не понравилось — «Вернуть маску»`, 'success', 9000);
    } catch (e) {
      показать('');
      log.warn('[Печень] сорвалось:', e.message);
      showToast('Печень: ' + e.message, 'error', 7000);
    } finally {
      _печеньИдёт = false;
      if (кнопка) { кнопка.disabled = false; }
    }
  });

  // [12.0.56] ЛЁГКИЕ АВТОМАТОМ — правое и левое за один запуск, по образцу печени
  // выше: та же дверь к данным, тот же холст, та же отмена «Вернуть маску».
  // Сеть lungmask, если файл лежит; нет — по плотности (thoraxCore), и кнопка
  // говорит, чем посчитано. Конвейер — ai/lungSeg.js, математика — ai/lungMath.js.
  // Прежде кнопка (тогда карточка «Лёгкие») не находила лёгких никогда.
  let _лёгкиеИдут = false;
  on('btn-ai-lung-seg', 'click', async () => {
    if (_лёгкиеИдут) { showToast('Лёгкие уже считаются — дождитесь конца', 'info', 2500); return; }
    const кнопка = document.getElementById('btn-ai-lung-seg');
    const строка = document.getElementById('lungs-auto-hod');
    const показать = (т) => { if (строка) строка.textContent = т; };
    _лёгкиеИдут = true;
    if (кнопка) кнопка.disabled = true;
    const держим = await _держатьСнимок();
    try {
      const { лёгкиеАвтоматом } = await import('../ai/lungSeg.js');
      const р = await лёгкиеАвтоматом({
        сегмент: _активныйНомерСегмента(),
        ход: (доля, текст) => показать(`${Math.round(доля * 100)}% · ${текст}`),
      });
      if (!р.ок) {
        показать('');
        log.warn('[Лёгкие] не вышло:', р.причина);
        showToast('Лёгкие не нашлись: ' + р.причина, 'warning', 9000);
        return;
      }
      const итог = `правое ${р.правоеМл.toFixed(0)} мл (сегмент ${р.сегменты[0]}), левое ${р.левоеМл.toFixed(0)} мл (сегмент ${р.сегменты[1]})`;
      показать(`${итог} · ${р.способ}`);
      const гдеВидно = _тсГдеВиднаМаска();
      showToast(`Лёгкие: ${итог}. Посчитано: ${р.способ}, за ${(р.мс / 1000).toFixed(0)} с.`
        + (р.слиты ? ' Лёгкие на снимке слились в один кусок — разделены по середине тела на каждом срезе.' : '')
        + (гдеВидно.предупредить
          ? ` Маска рисуется только в срезовых окнах${гдеВидно.окна.length ? ` (${гдеВидно.окна.join(', ')})` : ''}; в «Нативных срезах» её нет.`
          : '')
        + ' Не понравилось — «Вернуть маску».', 'success', 14000);
    } catch (e) {
      показать('');
      log.warn('[Лёгкие] сорвалось:', e.message);
      showToast('Лёгкие: ' + e.message, 'error', 7000);
    } finally {
      await _отпуститьСнимок(держим);
      await _проверитьСнимок('поиск лёгких');
      _лёгкиеИдут = false;
      if (кнопка) кнопка.disabled = false;
    }
  });

  // [11.1.29] Значки движка карточкам с data-model — ПО ФАКТУ (аудит §2.1).
  // [12.0.56] Значки «алгоритм/нейросеть» карточкам с data-model — сняты вместе с
  // карточками (надгробие — index.html, вкладка ИИ).

  // [12.0.56] Второй обработчик карточек ИИ (.ai-run-btn) УДАЛЁН. Он вёл lungmask —
  // единственную настоящую сеть среди карточек — но отступал, не начав: модель
  // числилась «алгоритмическим откатом». Да и путь был неполный: срез целиком без
  // рамки тела, без разворота осей, без чистки, маска — в отдельную сегментацию
  // мимо «Сегментации». Лёгкие теперь — кнопка «Лёгкие автоматом» (выше, у печени).

  // [10.19.28] Пять незаконченных кнопок УДАЛЕНЫ по решению владельца, а не
  // помечены: «Маркировка коронаров», «Смещение средней линии», «Нумерация
  // позвонков», «Трассировка сосудов», «Калибровка линейки». В 10.19.9 их
  // пометили заглушками, чтобы они перестали врать, — но кнопка, которая честно
  // говорит «не реализовано», всё равно занимает место и обещает продолжение.
  // Здесь стоял их список с текстами отказов; вместе с кнопками ушёл и он.
  //
  // Что осталось вместо них, слово в слово из тех же пояснений: кальциевый счёт
  // считается по-настоящему, но без разбивки по LAD/LCx/RCA; смещение средней
  // линии измеряется «Линейкой» — она калибрована по DICOM и даёт миллиметры;
  // BMD по Pickhardt работает, уровень позвонка врач указывает сам; диаметр
  // аорты измеряется; размер пикселя берётся из DICOM, поэтому ручная
  // калибровка не нужна (и опасна: неверная молча испортит ВСЕ измерения).

  log.debug('[ClinicalTools] ✅ Все инструменты привязаны');
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Batch analysis, report generator, history
// ══════════════════════════════════════════════════════════════

/** @type {Array<{tool:string, result:Object, timestamp:number}>} */
const _clinicalHistory = [];
const CLINICAL_HISTORY_MAX = 50;

/**
 * Get clinical analysis history
 * @returns {Array}
 */
export function getClinicalHistory() { return [..._clinicalHistory]; }

/**
 * Clear clinical history
 */
export function clearClinicalHistory() { _clinicalHistory.length = 0; }

/**
 * Run batch analysis — multiple clinical tools at once
 * @param {string} volumeId
 * @param {string[]} tools — e.g. ['agatston', 'ctr', 'emphysema']
 * @returns {Promise<Object>} results keyed by tool name
 */
export async function runBatchAnalysis(volumeId, tools = []) {
  try {
    const results = {};
    const toolMap = {
      'agatston': runAgatstonScore,
      'emphysema': runEmphysemaQuantification,
      'ctr': runCTR,
      'bmd': runBMD,
      // [12.0.52] 'pneumothorax', 'fracture', 'aorta' — функции удалены.
      'cardiac': runCardiacSegmentation,
    };
    for (const t of tools) {
      const fn = toolMap[t];
      if (!fn) { results[t] = { error: 'Unknown tool' }; continue; }
      try {
        results[t] = await fn(volumeId);
        _clinicalHistory.push({ tool: t, result: results[t], timestamp: Date.now() });
        if (_clinicalHistory.length > CLINICAL_HISTORY_MAX) _clinicalHistory.shift();
      } catch (e) { results[t] = { error: e.message }; }
    }
    return results;
  } catch (e) { log.warn(`[clinicalTools] runBatchAnalysis failed:`, e.message); return {}; }
}

/**
 * Export clinical history as CSV
 */
export function exportClinicalHistoryCSV() {
  if (!_clinicalHistory.length) return;
  const rows = [['Tool', 'Timestamp', 'Result Summary']];
  for (const h of _clinicalHistory) {
    const summary = JSON.stringify(h.result).substring(0, 200);
    rows.push([h.tool, new Date(h.timestamp).toISOString(), summary]);
  }
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `clinical_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Get list of all available clinical tools
 * @returns {string[]}
 */
export function listAvailableClinicalTools() {
  return ['agatston', 'emphysema', 'ctr', 'bmd', 'cardiac', 'regionGrow', 'medSAM'];
}

// [FIX] Cleanup all event listeners
export function destroyClinicalTools() {
  _ac.abort();
  _ac = new AbortController();
}
